/* The public face of the always-on station room.
 *
 * Desktop is a non-modal region anchored to the persistent player: somebody
 * can keep the room open and still tune, pause or browse. At the same 760px
 * breakpoint where the player becomes one row, chat becomes a full-screen
 * modal with the player's proven focus-trap and body-lock behaviour.
 *
 * The room is a small social club: recent lines become speech bubbles on the
 * floor while the complete moderated log stays available in the rail. A full
 * snapshot may remove a staff-hidden line, so React keys are server ids and
 * nothing is indexed.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';

import { ApiError } from '../api';
import { ErrorNote, Spinner, useNarrow } from '../components';
import type {
  ChatAction,
  ChatAvatar,
  ChatCostume,
  ChatDance,
  ChatDrink,
  ChatFloorRoom,
  ChatHair,
  ChatMessage,
  ChatMovement,
  ChatPresence,
  ChatProfile,
  ChatSender,
  ChatShirt,
  ChatSkin,
  ChatSpaDoorState,
  ChatTattoo,
  ChatTrousers,
  ChatVenueRoom,
} from '../types';
import { ArcadeChase } from './ArcadeChase';
import { useChat } from './ChatProvider';
import type { ChatPhase, ChatValue } from './ChatProvider';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Floor speech is momentary; the complete conversation remains in the rail. */
const CHAT_BUBBLE_LIFETIME_MS = 10_000;

const AVATARS: readonly { token: ChatAvatar; glyph: string; label: string }[] = [
  { token: 'signal', glyph: '◎', label: 'Signal' },
  { token: 'vinyl', glyph: '◉', label: 'Vinyl' },
  { token: 'wave', glyph: '≋', label: 'Wave' },
  { token: 'star', glyph: '✦', label: 'Star' },
  { token: 'moon', glyph: '◐', label: 'Moon' },
  { token: 'bolt', glyph: '↯', label: 'Bolt' },
  { token: 'lola', glyph: '♀', label: 'Lola' },
  { token: 'mina', glyph: '✿', label: 'Mina' },
  { token: 'sol', glyph: '☼', label: 'Sol' },
  { token: 'luna', glyph: '☾', label: 'Luna' },
  { token: 'echo', glyph: '⟨⟩', label: 'Echo' },
  { token: 'aria', glyph: '♪', label: 'Aria' },
  { token: 'hgr-admin', glyph: 'H', label: 'HGR admin character' },
  { token: 'hgr-police', glyph: '★', label: 'Policeman admin character' },
] as const;

function isAdminAvatar(avatar: ChatAvatar): boolean {
  return avatar === 'hgr-admin' || avatar === 'hgr-police';
}

// The colours themselves live in styles/world.css as --fig-skin-*, and the
// swatch here renders that variable rather than a copy of the hex, so the
// picker and the sprite cannot drift apart. Numbered rather than named: any
// adjective for a skin tone is somebody's description of a person, and a
// number is not.
const SKINS: readonly { token: ChatSkin; swatch: string; label: string }[] = [
  { token: 'tone-1', swatch: 'var(--fig-skin-1)', label: 'Skin tone 1' },
  { token: 'tone-2', swatch: 'var(--fig-skin-2)', label: 'Skin tone 2' },
  { token: 'tone-3', swatch: 'var(--fig-skin-3)', label: 'Skin tone 3' },
  { token: 'tone-4', swatch: 'var(--fig-skin-4)', label: 'Skin tone 4' },
  { token: 'tone-5', swatch: 'var(--fig-skin-5)', label: 'Skin tone 5' },
  { token: 'tone-6', swatch: 'var(--fig-skin-6)', label: 'Skin tone 6' },
  { token: 'tone-7', swatch: 'var(--fig-skin-7)', label: 'Skin tone 7' },
  { token: 'tone-8', swatch: 'var(--fig-skin-8)', label: 'Skin tone 8' },
  { token: 'tone-9', swatch: 'var(--fig-skin-9)', label: 'Skin tone 9' },
] as const;

// Same arrangement: --fig-hair-* in world.css is the one list. Nine natural,
// silver, and two dyed in the station's own pink and aqua.
const HAIRS: readonly { token: ChatHair; swatch: string; label: string }[] = [
  { token: 'black', swatch: 'var(--fig-hair-black)', label: 'Black' },
  { token: 'blue-black', swatch: 'var(--fig-hair-blue-black)', label: 'Blue black' },
  { token: 'dark-brown', swatch: 'var(--fig-hair-dark-brown)', label: 'Dark brown' },
  { token: 'auburn', swatch: 'var(--fig-hair-auburn)', label: 'Auburn' },
  { token: 'ash-brown', swatch: 'var(--fig-hair-ash-brown)', label: 'Ash brown' },
  { token: 'copper', swatch: 'var(--fig-hair-copper)', label: 'Copper' },
  { token: 'mid-brown', swatch: 'var(--fig-hair-mid-brown)', label: 'Mid brown' },
  { token: 'caramel', swatch: 'var(--fig-hair-caramel)', label: 'Caramel' },
  { token: 'blonde', swatch: 'var(--fig-hair-blonde)', label: 'Blonde' },
  { token: 'silver', swatch: 'var(--fig-hair-silver)', label: 'Silver' },
  { token: 'pink', swatch: 'var(--fig-hair-pink)', label: 'Dyed pink' },
  { token: 'aqua', swatch: 'var(--fig-hair-aqua)', label: 'Dyed aqua' },
] as const;

const TATTOOS: readonly { token: ChatTattoo; glyph: string; label: string }[] = [
  { token: 'none', glyph: '○', label: 'None' },
  { token: 'hgr', glyph: 'H', label: 'HGR' },
  { token: 'star', glyph: '✦', label: 'Star' },
  { token: 'wave', glyph: '≋', label: 'Wave' },
  { token: 'heart', glyph: '♥', label: 'Heart' },
] as const;

const DANCES: readonly { token: ChatDance; label: string }[] = [
  { token: 'sway', label: 'Sway' },
  { token: 'bounce', label: 'Bounce' },
  { token: 'shuffle', label: 'Shuffle' },
  { token: 'spin', label: 'Spin' },
  { token: 'robot', label: 'Robot' },
  { token: 'jump', label: 'Jump' },
] as const;

const MOVEMENTS: readonly { token: ChatMovement; label: string }[] = [
  { token: 'rooted', label: 'Stay put' },
  { token: 'glide', label: 'Side glide' },
  { token: 'orbit', label: 'Orbit' },
  { token: 'zigzag', label: 'Zigzag' },
  { token: 'moonwalk', label: 'Moonwalk' },
  { token: 'float', label: 'Float' },
] as const;

const SHIRTS: readonly { token: ChatShirt; label: string }[] = [
  { token: 'hgr-tee', label: 'HGR tee' },
  { token: 'mesh', label: 'Mesh top' },
  { token: 'track', label: 'Track top' },
  { token: 'sunset', label: 'Sunset knit' },
  { token: 'workshirt', label: 'Workshirt' },
  { token: 'no-shirt', label: 'No shirt' },
  { token: 'hgr-limited', label: 'HGR limited tee' },
  { token: 'rr-tee', label: 'RR tee' },
  { token: 'hgr-radio', label: 'HGR radio tee' },
  { token: 'flytree', label: 'FlyTree Records tee' },
] as const;

const ACTIONS: readonly { token: ChatAction; label: string }[] = [
  { token: 'idle', label: 'Just dance' },
  { token: 'hands-up', label: 'Hands up' },
  { token: 'pushups', label: 'Push-ups' },
  { token: 'clap', label: 'Clap' },
  { token: 'wave', label: 'Wave' },
  { token: 'point', label: 'Point' },
] as const;

const TROUSERS: readonly { token: ChatTrousers; label: string }[] = [
  { token: 'black-jeans', label: 'Black jeans' },
  { token: 'denim', label: 'Blue denim' },
  { token: 'cargo', label: 'Sand cargos' },
  { token: 'track', label: 'Track pants' },
  { token: 'shorts', label: 'Club shorts' },
] as const;

const COSTUMES: readonly { token: ChatCostume; label: string }[] = [
  { token: 'none', label: 'No costume' },
  { token: 'cowboy', label: 'Cowboy' },
  { token: 'alien', label: 'Alien' },
  { token: 'scuba', label: 'Diver' },
  { token: 'wizard', label: 'Wizard' },
  { token: 'bee', label: 'Bee' },
  { token: 'cat', label: 'Cat' },
  { token: 'robe', label: 'Spa robe' },
  { token: 'mummy', label: 'Toilet-roll mummy' },
  { token: 'trex', label: 'Inflatable T-rex' },
  { token: 'disco', label: 'Disco ball head' },
] as const;

const DJ_ACTIONS = [
  { token: 'playing', label: 'PLAYING' },
  { token: 'dancing', label: 'DANCING' },
  { token: 'cueing', label: 'CUEING' },
  { token: 'vinyl', label: 'NEW VINYL' },
  { token: 'mixing', label: 'MIXING' },
] as const;
type DjAction = (typeof DJ_ACTIONS)[number]['token'];

const BAR_DRINKS: readonly { token: ChatDrink; label: string; short: string }[] = [
  { token: 'water', label: 'Still water', short: 'H₂O' },
  { token: 'cola', label: 'Cola', short: 'COLA' },
  { token: 'lemonade', label: 'Lemonade', short: 'LEMON' },
  { token: 'ginger-beer', label: 'Ginger beer', short: 'GINGER' },
  { token: 'beer', label: 'Cold beer', short: 'BEER' },
];

// What is in the crate by the sofas. These are records the station actually
// rates rather than filler, so each one is three things: the sleeve you pulled,
// what you say when you see it, and a release page. The whole sleeve keeps
// moving through the crate; the separate source label belongs to the record
// currently in your hand.
const RECORD_CRATE_FINDS: readonly {
  sleeve: string;
  note: string;
  releaseUrl: string;
  releaseSource: 'BANDCAMP' | 'DISCOGS';
}[] = [
  {
    sleeve: 'EDENFRANZ · CONTACT EP',
    note: 'BIG BANGER',
    releaseUrl: 'https://flytreerecords.bandcamp.com/album/contact-ep',
    releaseSource: 'BANDCAMP',
  },
  {
    sleeve: 'AEDIS',
    note: 'UAU, IS THIS MINIMAL?',
    releaseUrl: 'https://aedis.bandcamp.com/',
    releaseSource: 'BANDCAMP',
  },
  {
    sleeve: 'WITH A HEARTBEAT',
    note: "GLOSSY MISTAKES' LATEST",
    releaseUrl: 'https://glossymistakes.bandcamp.com/album/with-a-heartbeat',
    releaseSource: 'BANDCAMP',
  },
  {
    sleeve: 'LOS TRATRATRAX',
    note: 'REGGUETON VARIO',
    releaseUrl: 'https://tratratrax.bandcamp.com/',
    releaseSource: 'BANDCAMP',
  },
  {
    sleeve: 'LEXMIC · HORNY HOUSE',
    note: 'HORNY HOUSE',
    releaseUrl: 'https://flytreerecords.bandcamp.com/album/horny-house',
    releaseSource: 'BANDCAMP',
  },
  {
    sleeve: 'LOST SPACE DREAMS · SPACE26',
    note: 'SPACE26',
    releaseUrl: 'https://lostspacedreams.bandcamp.com/album/space26',
    releaseSource: 'BANDCAMP',
  },
  {
    sleeve: 'RAINDANCE',
    note: 'HEELS & SOULS',
    releaseUrl: 'https://heelsandsouls.bandcamp.com/album/raindance-nyika-nyika-hsrec009',
    releaseSource: 'BANDCAMP',
  },
  {
    sleeve: 'CHRIS REA · ON THE BEACH',
    note: 'BEACH CLASSIC',
    releaseUrl: 'https://www.discogs.com/es/master/114664-Chris-Rea-On-The-Beach',
    releaseSource: 'DISCOGS',
  },
  {
    sleeve: 'CHARANJIT SINGH',
    note: 'TEN RAGAS TO A DISCO BEAT',
    releaseUrl: 'https://www.discogs.com/es/release/14001962-Charanjit-Singh-Synthesizing-Ten-Ragas-To-A-Disco-Beat',
    releaseSource: 'DISCOGS',
  },
  {
    sleeve: 'LLUÍS LLACH',
    note: 'VIATGE A ITACA',
    releaseUrl: 'https://www.discogs.com/es/release/14547197-Llu%C3%ADs-Llach-Viatge-A-Itaca',
    releaseSource: 'DISCOGS',
  },
] as const;

// What comes out of the jar on the bar's low table, in order. The length is
// also how many the jar holds, so the fourth one really is the last one and
// the jar is drawn empty until somebody restocks it on the next press.
const COOKIE_JAR = [
  'CHOC CHIP · STILL WARM',
  'OATMEAL, SOMEHOW',
  'THE ONE WITH SPRINKLES',
  'THAT WAS THE LAST ONE',
] as const;

const ROOM_COPY: Record<ChatFloorRoom, {
  title: string;
  eyebrow: string;
  countLabel: string;
  ariaLabel: string;
}> = {
  beach: {
    title: 'Low Tide After Dark',
    eyebrow: 'HGE BEACH FREQUENCY · AFTER DARK',
    countLabel: 'on the night floor',
    ariaLabel: 'Higher Ground Radio after-dark dancefloor',
  },
  daylight: {
    title: 'Daylight Floor',
    eyebrow: 'HGE BEACH FREQUENCY · DAYLIGHT',
    countLabel: 'on the day floor',
    ariaLabel: 'Higher Ground Radio daylight dancefloor',
  },
  sunset: {
    title: 'Sunset Floor',
    eyebrow: 'HGE BEACH FREQUENCY · GOLDEN HOUR',
    countLabel: 'on the sunset floor',
    ariaLabel: 'Higher Ground Radio sunset dancefloor',
  },
  toilets: {
    title: 'The Toilets',
    eyebrow: 'HGR BACK ROOM · NO QUEUE CUTTING',
    countLabel: 'in the toilets',
    ariaLabel: 'Higher Ground Radio toilets',
  },
  lounge: {
    title: 'Balloon Lounge',
    eyebrow: 'HGR SIDE ROOM · TAKE IT EASY',
    countLabel: 'chilling',
    ariaLabel: 'Higher Ground Radio balloon lounge',
  },
  psychedelic: {
    title: 'The Other Side',
    eyebrow: 'HGR OPTICAL ROOM · LOOK, DO NOT TOUCH',
    countLabel: 'seeing patterns',
    ariaLabel: 'Higher Ground Radio optical room',
  },
  'ham-museum': {
    title: 'Museo del Jamón',
    eyebrow: 'HGR JAMÓN ROOM · CUT THIN, STAY LONG',
    countLabel: 'at the museum',
    ariaLabel: 'Higher Ground Radio ham museum',
  },
  'tattoo-studio': {
    title: 'Needle & Signal',
    eyebrow: 'HGR TATTOO STUDIO · PICK YOUR MARK',
    countLabel: 'at the tattoo studio',
    ariaLabel: 'Higher Ground Radio tattoo studio',
  },
  spa: {
    title: 'Night Spa',
    eyebrow: 'HGR NIGHT SPA · SAME FREQUENCY, WARM WATER',
    countLabel: 'in the pool',
    ariaLabel: 'Higher Ground Radio night spa and pool',
  },
  'club-terrace': {
    title: 'Higher Ground Experience Terrace',
    eyebrow: 'HGE BEACH CLUB · OPEN-AIR DECK',
    countLabel: 'on the terrace',
    ariaLabel: 'Higher Ground Experience beach club DJ terrace',
  },
  'inside-club': {
    title: 'HGE Beach Club',
    eyebrow: 'HGE BEACH CLUB · BAR & CHILL',
    countLabel: 'inside the club',
    ariaLabel: 'Higher Ground Experience indoor bar and chill room',
  },
  'sunset-beach': {
    title: 'HGE Sunset Beach',
    eyebrow: 'HGE BEACH CLUB · LAST LIGHT',
    countLabel: 'by the water',
    ariaLabel: 'Higher Ground Experience sunset beach and seating area',
  },
  'club-sundeck': {
    title: 'HGE Sundeck Terrace',
    eyebrow: 'HGE BEACH CLUB · SUNDECK',
    countLabel: 'on the sundeck',
    ariaLabel: 'Higher Ground Experience sundeck terrace',
  },
  'club-toilets': {
    title: 'The Long Drop',
    eyebrow: 'HGE BEACH CLUB · OUT THE BACK',
    countLabel: 'in the long drop',
    ariaLabel: 'The Long Drop, the beach club toilet block bar',
  },
  'arcade-chase': {
    title: 'The Getaway',
    eyebrow: 'HGE BACK ROOM · 30 SECOND RUN',
    countLabel: 'at the cabinet',
    ariaLabel: 'The Getaway, a thirty second side-scrolling arcade game',
  },
};

const MAIN_FLOORS: readonly {
  room: Extract<ChatFloorRoom, 'beach' | 'daylight' | 'sunset'>;
  label: string;
  name: string;
}[] = [
  { room: 'daylight', label: 'DAY', name: 'the Daylight Floor' },
  { room: 'sunset', label: 'SUNSET', name: 'the Sunset Floor' },
  { room: 'beach', label: 'NIGHT', name: 'Low Tide After Dark' },
];

const BEACH_CLUB_ROOMS: readonly {
  room: Extract<
    ChatFloorRoom,
    'club-terrace' | 'club-sundeck' | 'inside-club' | 'sunset-beach' | 'arcade-chase'
  >;
  label: string;
  name: string;
}[] = [
  { room: 'club-terrace', label: 'TERRACE', name: 'the Higher Ground Experience Terrace' },
  { room: 'club-sundeck', label: 'SUNDECK', name: 'the HGE Sundeck Terrace' },
  { room: 'inside-club', label: 'INSIDE', name: 'the HGE Beach Club' },
  { room: 'sunset-beach', label: 'BEACH', name: 'HGE Sunset Beach' },
  { room: 'arcade-chase', label: 'GAME', name: 'The Getaway arcade room' },
];

// The HGR side rooms off the main floor, in menu order. They are data rather
// than six near-identical buttons so one list decides what the floor offers,
// and so hiding one closes up the numbered slots instead of leaving a hole in
// the column.
const SIDE_ROOMS: readonly {
  room: Extract<
    ChatFloorRoom,
    'toilets' | 'lounge' | 'psychedelic' | 'ham-museum' | 'tattoo-studio' | 'spa'
  >;
  label: string;
  modifier?: string;
  fullLabel: string;
  enterLabel: string;
}[] = [
  { room: 'toilets', label: 'WC', fullLabel: 'The toilets are full', enterLabel: 'Enter the toilets' },
  {
    room: 'lounge',
    label: 'CHILL',
    modifier: 'lounge',
    fullLabel: 'The balloon lounge is full',
    enterLabel: 'Enter the balloon lounge',
  },
  {
    room: 'psychedelic',
    label: 'OPTIC',
    modifier: 'psychedelic',
    fullLabel: 'The optical room is full',
    enterLabel: 'Enter the optical room',
  },
  {
    room: 'ham-museum',
    label: 'JAMÓN',
    modifier: 'ham',
    fullLabel: 'Museo del Jamón is full',
    enterLabel: 'Enter Museo del Jamón',
  },
  {
    room: 'tattoo-studio',
    label: 'INK',
    modifier: 'tattoo',
    fullLabel: 'The tattoo studio is full',
    enterLabel: 'Enter the tattoo studio',
  },
  {
    room: 'spa',
    label: 'SPA',
    modifier: 'spa',
    fullLabel: 'The night spa is full',
    enterLabel: 'Enter the night spa',
  },
];

// Where a guest lands and what every exit leads back to. It must be a room the
// venue actually offers, so it is declared beside HIDDEN_ROOMS: hiding the home
// floor without moving this is how you strand everybody. The API agrees with it
// through the chat_guests.floor_room column default (migration 0030).
const HOME_ROOM: ChatFloorRoom = 'club-sundeck';

// True while the venue is only the beach club. The club menu carries an exit
// back to the main floor, which is worth nothing when the home room is itself a
// club room -- it would duplicate a door the menu already shows and promise a
// main venue the guest cannot reach.
const HOME_IS_IN_CLUB = BEACH_CLUB_ROOMS.some((room) => room.room === HOME_ROOM);

// Rooms the venue is not currently putting a door to. This hides the doors and
// nothing else: every scene and its CSS are still in the bundle, the API still
// accepts the room, and a guest already standing in one keeps the exit back to
// the bar. Offering a room again is deleting its slug from this set.
const HIDDEN_ROOMS: ReadonlySet<ChatFloorRoom> = new Set<ChatFloorRoom>([
  'beach',
  'daylight',
  'sunset',
  'toilets',
  'lounge',
  'psychedelic',
  'ham-museum',
  'tattoo-studio',
  'spa',
  'arcade-chase',
]);

// Rooms whose way out is not the home floor, and the label their door wears.
//
// This is the whole of "you can only get in from the bar". Every other room is
// reachable from a menu, which is exactly why they are interchangeable -- the
// menu decides where you may go, not the geography. The Long Drop is left out of
// BEACH_CLUB_ROOMS so no menu offers it, is not counted as being in the club so
// the club menu does not appear once you are inside, and is entered only by the
// WC door drawn in the club scene. The one thing left was the way back: the
// fallback exit returns a guest to HOME_ROOM, which for this room would be a
// one-way trip out of a bar you can only reach from that bar. So the door it
// came through is the door it goes back through.
const ROOM_EXIT: Partial<Record<ChatFloorRoom, { room: ChatFloorRoom; label: string }>> = {
  'club-toilets': { room: 'inside-club', label: 'BAR' },
  'arcade-chase': { room: 'club-sundeck', label: 'SUNDECK' },
};

// Where a guest lands when they walk in, as a percentage across the floor, so
// they appear at the door they used rather than inside the furniture. A full
// Record rather than a partial one: a new room should not compile until it has
// said where its door is.
const ROOM_ARRIVAL_X: Record<ChatFloorRoom, number> = {
  beach: 84,
  daylight: 84,
  sunset: 84,
  toilets: 18,
  lounge: 82,
  psychedelic: 50,
  'ham-museum': 26,
  'tattoo-studio': 74,
  spa: 50,
  'club-terrace': 50,
  'club-sundeck': 44,
  'inside-club': 50,
  'sunset-beach': 52,
  // In through the WC door on the left of the block, not on top of the bar.
  'club-toilets': 30,
  'arcade-chase': 18,
};

// The venue's lights. They are a light *level*, not a colour scheme: every
// surface in every room reads the same custom properties, so one step re-lights
// the sky, the sea, the timber, the cast shadows and the lamp glow together.
// The values live in styles/world.css; this is only the vocabulary.
type WorldLight = 'afternoon' | 'golden' | 'lanterns' | 'indoor';

// The three an admin can put an open-air room in: day, sunset, night. `indoor`
// is not among them because you cannot turn a deck into a room by pressing a
// button. The labels are the venue's own names for those three hours -- the
// same three the main floors are named after.
const DECK_LIGHT: readonly {
  token: Exclude<WorldLight, 'indoor'>;
  label: string;
}[] = [
  { token: 'afternoon', label: 'AFTERNOON' },
  { token: 'golden', label: 'GOLDEN HOUR' },
  { token: 'lanterns', label: 'LANTERNS' },
];

// The rooms whose light can be changed from inside them. All three are
// open-air: the sky is the light, so moving it is moving the hour. The club
// bar and The Long Drop are left out because their light is the building's.
const LIT_ROOMS: ReadonlySet<ChatFloorRoom> = new Set<ChatFloorRoom>([
  'club-terrace',
  'club-sundeck',
  'sunset-beach',
]);

// The parasols a guest can crank open and closed, one key per parasol rather
// than one boolean per room: the terrace has two, and they wind independently.
type ParasolId = 'terrace-corona' | 'terrace-fuze' | 'sundeck' | 'sunset-beach';

const PARASOLS_OPEN: Readonly<Record<ParasolId, boolean>> = {
  'terrace-corona': true,
  'terrace-fuze': true,
  sundeck: true,
  'sunset-beach': true,
};

// The terrace's two sponsored umbrellas. Somebody else's brand is the one thing
// on the deck allowed not to match, so the brand travels with the parasol
// rather than being typed into the scene twice.
const TERRACE_UMBRELLAS: readonly {
  id: Extract<ParasolId, 'terrace-corona' | 'terrace-fuze'>;
  modifier: string;
  brand: string;
}[] = [
  { id: 'terrace-corona', modifier: 'corona', brand: 'CORONA' },
  { id: 'terrace-fuze', modifier: 'fuze', brand: 'FUZE TEA' },
];

// The venue's lights, as filters. Tokens can recolour a CSS rig but not the
// pixels inside a spritesheet, and a listener is drawn both ways, so the light
// that reaches every character has to be a filter. Each one is the per-channel
// gain and offset fitted to the same hand-authored sundeck states the colour
// ramps came from; styles/world.css carries the derivation and the reason
// lanterns is damped. `afternoon` has no entry because it is the reference
// light -- --w-light-filter is `none` there, so it costs nothing.
//
// color-interpolation-filters="sRGB" is NOT optional. SVG filters default to
// linearRGB, and these were fitted in sRGB; without it every light is wrong.
type Transfer = readonly [slope: number, intercept: number];
const LIGHT_FILTERS: readonly {
  id: string;
  r: Transfer;
  g: Transfer;
  b: Transfer;
}[] = [
  { id: 'hgr-light-golden',   r: [0.9770, -0.0154], g: [0.9471, -0.0454], b: [0.8393, -0.0044] },
  { id: 'hgr-light-lanterns', r: [0.9635, -0.0665], g: [1.0749, -0.1476], b: [1.0636, -0.1043] },
  { id: 'hgr-light-indoor',   r: [0.9273,  0.0084], g: [0.8184,  0.0339], b: [0.7588,  0.0497] },
];

function VenueLightFilters() {
  return (
    <svg className="chat-lightdefs" aria-hidden="true" focusable="false">
      <defs>
        {LIGHT_FILTERS.map((light) => (
          <filter id={light.id} colorInterpolationFilters="sRGB" key={light.id}>
            <feComponentTransfer>
              <feFuncR type="linear" slope={light.r[0]} intercept={light.r[1]} />
              <feFuncG type="linear" slope={light.g[0]} intercept={light.g[1]} />
              <feFuncB type="linear" slope={light.b[0]} intercept={light.b[1]} />
            </feComponentTransfer>
          </filter>
        ))}
      </defs>
    </svg>
  );
}

/* The venue's light switch: a box on the wall of whichever open-air room it is
   standing in, showing the hour as three filled pips. It is STAFF EQUIPMENT --
   it re-grades the room for the person pressing it, so it is rendered only for
   an admin account and is absent, not disabled, for everybody else. */
function RoomLightSwitch({
  light,
  onCycle,
}: {
  light: (typeof DECK_LIGHT)[number];
  onCycle: () => void;
}) {
  return (
    <button
      type="button"
      className="chat-floor__lightswitch mono"
      onClick={onCycle}
      aria-label={`Room light: ${light.label}. Change it.`}
    >
      <i aria-hidden="true"><em /><em /><em /></i>
      <span>{light.label}</span>
    </button>
  );
}

// Which light each room stands in. This is the whole mapping, and keeping it as
// data is what stops a room from growing its own palette again: a room owns its
// shapes, and the light owns its colours. A room absent from here gets the
// default `afternoon` ramp, which is complete — the nine rooms still on their
// own literals are simply not converted yet.
const ROOM_LIGHT: Partial<Record<ChatFloorRoom, WorldLight>> = {
  'club-terrace': 'lanterns',
  'club-sundeck': 'lanterns',
  'sunset-beach': 'lanterns',
  'inside-club': 'indoor',

  // Out the back of an indoor room, but not indoors: a tin roof, a gap onto the
  // sea and a run of warm bulbs. That is what the lanterns ramp was fitted to,
  // so The Long Drop borrows the night floor's light rather than the club's.
  'club-toilets': 'lanterns',

  // The three main floors are one room at three times of day -- that was always
  // the intent, it was just built as three rooms with three hand-typed
  // palettes. These three lines are now the whole of the difference between
  // them; the shapes are written once in components.css.
  daylight: 'afternoon',
  sunset: 'golden',
  beach: 'lanterns',
};

const SUNDECK_SIGHTINGS = [
  'A VERY SLOW CARGO SHIP',
  'TWO KAYAKS, ONE OAR',
  'THE FERRY, RUNNING LATE',
  'A SWIMMER, TOO FAR OUT',
  'A FIN. A DOLPHIN?',
  'THE ICE BOAT, LATE AGAIN',
] as const;

function currentDjAction(): DjAction {
  return DJ_ACTIONS[Math.floor(Date.now() / 60_000) % DJ_ACTIONS.length].token;
}

function djActionLabel(action: DjAction): string {
  return DJ_ACTIONS.find((item) => item.token === action)?.label ?? 'PLAYING';
}

function StationDjBooth({ action, brand = 'HGR', terrace = false }: {
  action: DjAction;
  brand?: 'HGR' | 'HGE';
  terrace?: boolean;
}) {
  return (
    <div
      className={`chat-floor__booth${terrace ? ' chat-floor__booth--terrace' : ''}`}
      data-dj-action={action}
      aria-hidden="true"
    >
      <span className="chat-floor__roof" />
      <span className="chat-floor__cable chat-floor__cable--left" />
      <span className="chat-floor__cable chat-floor__cable--right" />
      <span className="chat-floor__dj">
        <span className="chat-floor__dj-head">
          <span className="chat-floor__headphones" />
        </span>
        <span className="chat-floor__dj-body" />
        <span className="chat-floor__dj-arm chat-floor__dj-arm--left" />
        <span className="chat-floor__dj-arm chat-floor__dj-arm--right" />
      </span>
      <span className="chat-floor__console">
        <span className="chat-floor__turntable chat-floor__turntable--left">
          <span className="chat-floor__platter" />
          <span className="chat-floor__tonearm" />
        </span>
        <span className="chat-floor__mixer">
          <span className="chat-floor__levels"><b /><b /><b /><b /></span>
          <span className="chat-floor__channels"><b /><b /><b /></span>
          <span className="chat-floor__crossfader" />
        </span>
        <span className="chat-floor__turntable chat-floor__turntable--right">
          <span className="chat-floor__platter" />
          <span className="chat-floor__tonearm" />
        </span>
      </span>
      <small><span />LIVE</small>
      <span className="chat-floor__booth-front">
        <b>{brand}</b>
        <span className="chat-floor__vu"><span /><span /><span /><span /><span /></span>
      </span>
      <span className="chat-floor__dj-action">{djActionLabel(action)}</span>
    </div>
  );
}

function avatarGlyph(token: ChatAvatar): string {
  return AVATARS.find((avatar) => avatar.token === token)?.glyph ?? '◎';
}

function danceLabel(token: ChatDance): string {
  return DANCES.find((dance) => dance.token === token)?.label ?? 'Sway';
}

function movementLabel(token: ChatMovement): string {
  return MOVEMENTS.find((movement) => movement.token === token)?.label ?? 'Stay put';
}

function shirtLabel(token: ChatShirt): string {
  return SHIRTS.find((shirt) => shirt.token === token)?.label ?? 'HGR tee';
}

function trousersLabel(token: ChatTrousers): string {
  return TROUSERS.find((trousers) => trousers.token === token)?.label ?? 'Black jeans';
}

function costumeLabel(token: ChatCostume): string {
  return COSTUMES.find((costume) => costume.token === token)?.label ?? 'No costume';
}

function memberLabel(value: string): string {
  const at = new Date(value);
  if (!Number.isFinite(at.getTime())) return 'Station listener';
  return `Member since ${new Intl.DateTimeFormat(undefined, {
    month: 'short',
    year: 'numeric',
  }).format(at)}`;
}

function AvatarPicker({
  value,
  shirt,
  trousers,
  isAdmin,
  onChange,
}: {
  value: ChatAvatar;
  shirt: ChatShirt;
  trousers: ChatTrousers;
  isAdmin: boolean;
  onChange: (avatar: ChatAvatar) => void;
}) {
  const availableAvatars = AVATARS.filter((avatar) => !isAdminAvatar(avatar.token) || isAdmin);
  return (
    <fieldset className="chat-avatars">
      <legend className="mono">Choose your character</legend>
      <div className="chat-avatars__grid">
        {availableAvatars.map((avatar) => (
          <button
            type="button"
            className="chat-avatar chat-avatar--choice"
            data-selected={avatar.token === value ? 'true' : undefined}
            aria-pressed={avatar.token === value}
            aria-label={avatar.label}
            key={avatar.token}
            onClick={() => onChange(avatar.token)}
          >
            <span className="chat-avatar__sprite">
              <PixelDancer avatar={avatar.token} shirt={shirt} trousers={trousers} />
            </span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}

/** Skin and hair are swatch grids rather than dancer previews. A preview would
 *  have to pick a hairstyle to show the tone on, which puts a character in
 *  front of a choice that is deliberately not about characters. */
function ComplexionPicker<T extends string>({
  legend,
  options,
  value,
  onChange,
}: {
  legend: string;
  options: readonly { token: T; swatch: string; label: string }[];
  value: T;
  onChange: (token: T) => void;
}) {
  return (
    <fieldset className="chat-swatches">
      <legend className="mono">{legend}</legend>
      <div className="chat-swatches__grid">
        {options.map((option) => (
          <button
            type="button"
            className="chat-swatch"
            data-selected={option.token === value ? 'true' : undefined}
            aria-pressed={option.token === value}
            aria-label={option.label}
            title={option.label}
            key={option.token}
            onClick={() => onChange(option.token)}
          >
            <span className="chat-swatch__chip" style={{ background: option.swatch }} />
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function DancePicker({
  avatar,
  shirt,
  trousers,
  value,
  onChange,
}: {
  avatar: ChatAvatar;
  shirt: ChatShirt;
  trousers: ChatTrousers;
  value: ChatDance;
  onChange: (dance: ChatDance) => void;
}) {
  return (
    <fieldset className="chat-dances">
      <legend className="mono">Choose your dance</legend>
      <div className="chat-dances__grid">
        {DANCES.map((dance) => (
          <button
            type="button"
            className="chat-dance"
            data-selected={dance.token === value ? 'true' : undefined}
            aria-pressed={dance.token === value}
            aria-label={dance.label}
            title={dance.label}
            key={dance.token}
            onClick={() => onChange(dance.token)}
          >
            <span className="chat-dance__sprite">
              <PixelDancer avatar={avatar} dance={dance.token} shirt={shirt} trousers={trousers} />
            </span>
            <span className="chat-dance__label mono">{dance.label}</span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function MovementPicker({
  avatar,
  dance,
  shirt,
  trousers,
  value,
  onChange,
}: {
  avatar: ChatAvatar;
  dance: ChatDance;
  shirt: ChatShirt;
  trousers: ChatTrousers;
  value: ChatMovement;
  onChange: (movement: ChatMovement) => void;
}) {
  return (
    <fieldset className="chat-movements">
      <legend className="mono">Choose your movement</legend>
      <div className="chat-movements__grid">
        {MOVEMENTS.map((movement) => (
          <button
            type="button"
            className="chat-move"
            data-selected={movement.token === value ? 'true' : undefined}
            aria-pressed={movement.token === value}
            aria-label={movement.label}
            title={movement.label}
            key={movement.token}
            onClick={() => onChange(movement.token)}
          >
            <span className="chat-move__sprite">
              <span className="chat-move__walker" data-movement={movement.token}>
                <PixelDancer avatar={avatar} dance={dance} shirt={shirt} trousers={trousers} />
              </span>
            </span>
            <span className="chat-move__label mono">{movement.label}</span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function WardrobePicker({
  avatar,
  skin,
  hair,
  dance,
  shirt,
  trousers,
  costume,
  onShirtChange,
  onTrousersChange,
  onCostumeChange,
  availableShirts,
}: {
  avatar: ChatAvatar;
  /** The preview is "your look", so it shows your complexion, not the
   *  character's -- unlike the shape pickers above it. */
  skin: ChatSkin;
  hair: ChatHair;
  dance: ChatDance;
  shirt: ChatShirt;
  trousers: ChatTrousers;
  costume: ChatCostume;
  onShirtChange: (shirt: ChatShirt) => void;
  onTrousersChange: (trousers: ChatTrousers) => void;
  onCostumeChange: (costume: ChatCostume) => void;
  availableShirts: ReadonlySet<ChatShirt>;
}) {
  return (
    <fieldset className="chat-wardrobe">
      <legend className="mono">Build your outfit</legend>
      <div className="chat-wardrobe__layout">
        <div className="chat-wardrobe__preview" aria-label={`${shirtLabel(shirt)} with ${trousersLabel(trousers)} and ${costumeLabel(costume)}`}>
          <PixelDancer
            avatar={avatar}
            skin={skin}
            hair={hair}
            dance={dance}
            shirt={shirt}
            trousers={trousers}
            costume={costume}
          />
          <span className="mono">Your look</span>
        </div>
        <div className="chat-wardrobe__rack">
          <span className="mono chat-wardrobe__title">Shirts</span>
          <div className="chat-wardrobe__choices">
            {SHIRTS.filter((item) => availableShirts.has(item.token)).map((item) => (
              <button
                type="button"
                className="chat-wardrobe__choice"
                data-kind="shirt"
                data-token={item.token}
                data-selected={item.token === shirt ? 'true' : undefined}
                aria-pressed={item.token === shirt}
                onClick={() => onShirtChange(item.token)}
                key={item.token}
              >
                <i aria-hidden="true" />
                <span>{item.label}</span>
              </button>
            ))}
          </div>
          <span className="mono chat-wardrobe__title">Trousers</span>
          <div className="chat-wardrobe__choices">
            {TROUSERS.map((item) => (
              <button
                type="button"
                className="chat-wardrobe__choice"
                data-kind="trousers"
                data-token={item.token}
                data-selected={item.token === trousers ? 'true' : undefined}
                aria-pressed={item.token === trousers}
                onClick={() => onTrousersChange(item.token)}
                key={item.token}
              >
                <i aria-hidden="true" />
                <span>{item.label}</span>
              </button>
            ))}
          </div>
          <span className="mono chat-wardrobe__title">Costumes</span>
          <div className="chat-costumes__choices">
            {COSTUMES.map((item) => (
              <button
                type="button"
                className="chat-costume__choice"
                data-token={item.token}
                data-selected={item.token === costume ? 'true' : undefined}
                aria-pressed={item.token === costume}
                onClick={() => onCostumeChange(item.token)}
                title={item.label}
                key={item.token}
              >
                <span className="chat-costume__sprite" aria-hidden="true">
                  <PixelDancer avatar={avatar} dance="sway" shirt={shirt} trousers={trousers} costume={item.token} />
                </span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </fieldset>
  );
}

function ProfileCard({
  name,
  tag,
  profile,
  onClose,
}: {
  name: string;
  tag?: string;
  profile: ChatProfile;
  onClose?: () => void;
}) {
  const progress = profile.next_badge;
  return (
    <section className="chat-profile" aria-label={`${name}'s listener profile`}>
      <div className="chat-profile__top">
        <span className="chat-avatar chat-avatar--profile" aria-hidden="true">
          {avatarGlyph(profile.avatar)}
        </span>
        <div className="chat-profile__identity">
          <strong>{name}</strong>
          <span className="mono dim">
            {tag ? `#${tag} · ` : ''}{profile.rank}
          </span>
        </div>
        {onClose ? (
          <button
            type="button"
            className="chat-profile__close"
            onClick={onClose}
            aria-label="Close listener profile"
          >
            ×
          </button>
        ) : null}
      </div>
      <div className="mono chat-profile__stats">
        {profile.message_count} {profile.message_count === 1 ? 'signal' : 'signals'} ·{' '}
        {shirtLabel(profile.shirt)} · {trousersLabel(profile.trousers)} ·{' '}
        {costumeLabel(profile.costume)} ·{' '}
        {danceLabel(profile.dance)} · {movementLabel(profile.movement)} ·{' '}
        {memberLabel(profile.member_since)}
      </div>
      {profile.badges.length > 0 ? (
        <div className="chat-profile__badges" aria-label="Earned badges">
          {profile.badges.map((badge) => (
            <span className="chat-badge" key={badge.slug} title={badge.description}>
              {badge.title}
            </span>
          ))}
        </div>
      ) : (
        <p className="mono dim chat-profile__emptybadges">First badge unlocks with one signal.</p>
      )}
      {progress ? (
        <div className="chat-profile__progress">
          <div className="mono dim">
            Next: {progress.title} · {progress.current}/{progress.target}
          </div>
          <progress value={progress.current} max={progress.target}>
            {progress.current} of {progress.target}
          </progress>
        </div>
      ) : (
        <div className="mono dim chat-profile__complete">All current badges unlocked.</div>
      )}
    </section>
  );
}

function bubbleCopy(body: string): string {
  const singleLine = body.replace(/\s+/g, ' ').trim();
  return singleLine.length > 54 ? `${singleLine.slice(0, 51)}…` : singleLine;
}

function PixelDancer({
  avatar,
  skin,
  hair,
  dance = 'sway',
  shirt = 'hgr-tee',
  trousers = 'black-jeans',
  hasWater = false,
  drink = 'none',
  isRelaxing = false,
  tattoo = 'none',
  costume = 'none',
  action = 'idle',
}: {
  avatar: ChatAvatar;
  /** Undefined leaves the character's own colour showing, which is what the
   *  wardrobe previews want: they are picking a hair SHAPE, so they should not
   *  each assert a complexion. */
  skin?: ChatSkin;
  hair?: ChatHair;
  dance?: ChatDance;
  shirt?: ChatShirt;
  trousers?: ChatTrousers;
  hasWater?: boolean;
  drink?: ChatDrink;
  isRelaxing?: boolean;
  tattoo?: ChatTattoo;
  costume?: ChatCostume;
  action?: ChatAction;
}) {
  // PixelLab characters are full-body collectibles, so they deliberately do
  // not inherit the modular wardrobe rig. Until the remaining animation rows
  // are approved, deliberate hand actions fall back to the modular character.
  const showPixelLabPreview = isAdminAvatar(avatar) && action === 'idle';

  return (
    <span
      className={`chat-dancer${showPixelLabPreview ? ' chat-dancer--pixellab-preview' : ''}`}
      data-look={avatar}
      data-skin={skin}
      data-hair={hair}
      data-dance={dance}
      data-shirt={shirt}
      data-trousers={trousers}
      data-water={hasWater ? 'true' : undefined}
      data-drink={drink}
      data-relaxing={isRelaxing ? 'true' : undefined}
      data-tattoo={tattoo}
      data-costume={costume}
      data-action={action}
      aria-hidden="true"
    >
      <span className="chat-dancer__move">
        {showPixelLabPreview ? (
          <span className="chat-dancer__pixellab" />
        ) : (
          <>
            <span className="chat-dancer__hair" />
            <span className="chat-dancer__head"><i /></span>
            <span className="chat-dancer__body" />
            <span className="chat-dancer__arms"><i /><i /></span>
            <span className="chat-dancer__legs"><i /><i /></span>
            <span className="chat-dancer__shoes"><i /><i /></span>
            {tattoo !== 'none' ? <span className="chat-dancer__tattoo" /> : null}
            {costume !== 'none' ? <span className="chat-dancer__costume"><i /><b /><em /></span> : null}
          </>
        )}
        {hasWater || drink !== 'none' ? <span className="chat-dancer__water"><i /></span> : null}
      </span>
    </span>
  );
}

function SpaDoorControls({
  door,
  busy,
  onBouncer,
  onDecision,
}: {
  door: ChatSpaDoorState;
  busy: boolean;
  onBouncer: (active: boolean) => Promise<boolean>;
  onDecision: (requestId: string, approved: boolean, message: string) => Promise<boolean>;
}) {
  const [replies, setReplies] = useState<Record<string, string>>({});
  const isBouncer = Boolean(door.bouncer?.is_me);

  return (
    <section className="chat-spa-door" aria-label="Night Spa door">
      <header>
        <span className="mono">SPA DOOR</span>
        <strong>{isBouncer ? 'You are the bouncer' : door.bouncer ? `${door.bouncer.name} is on door` : 'Door is open'}</strong>
        {isBouncer ? (
          <button type="button" disabled={busy} onClick={() => void onBouncer(false)}>
            Leave door
          </button>
        ) : !door.bouncer ? (
          <button type="button" disabled={busy} onClick={() => void onBouncer(true)}>
            {busy ? 'One sec…' : 'Be the bouncer'}
          </button>
        ) : null}
      </header>
      {isBouncer ? (
        <div className="chat-spa-door__queue">
          {door.requests.length === 0 ? (
            <span className="mono">No one waiting.</span>
          ) : door.requests.map((entry) => {
            const reply = replies[entry.id] ?? '';
            return (
              <article key={entry.id}>
                <div><strong>{entry.visitor.name}</strong><span className="mono">#{entry.visitor.tag}</span></div>
                <p>“{entry.visitor_message}”</p>
                <input
                  value={reply}
                  maxLength={120}
                  placeholder="Write your reply…"
                  aria-label={`Reply to ${entry.visitor.name}`}
                  onChange={(event) => setReplies((current) => ({
                    ...current,
                    [entry.id]: event.target.value,
                  }))}
                />
                <div className="chat-spa-door__actions">
                  <button
                    type="button"
                    disabled={busy || !reply.trim()}
                    onClick={() => void onDecision(entry.id, true, reply)}
                  >Let in</button>
                  <button
                    type="button"
                    disabled={busy || !reply.trim()}
                    onClick={() => void onDecision(entry.id, false, reply)}
                  >Not tonight</button>
                </div>
              </article>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

function DanceFloor({
  me,
  messages,
  people,
  rooms,
  onMove,
  onWater,
  onDrink,
  waterBusy,
  onRelax,
  relaxBusy,
  onTattoo,
  tattooBusy,
  onAction,
  actionBusy,
  onTrackId,
  spaDoor,
  spaDoorBusy,
  onSpaBouncer,
  onSpaRequest,
  onSpaDecision,
  onProfile,
  onEditLook,
  isAdmin,
}: {
  me: ChatValue['me'];
  messages: readonly ChatMessage[];
  people: readonly ChatPresence[];
  rooms: readonly ChatVenueRoom[];
  onMove: (x: number, y: number, floorRoom?: ChatFloorRoom) => Promise<boolean>;
  onWater: (hasWater: boolean) => Promise<boolean>;
  onDrink: (drink: ChatDrink) => Promise<boolean>;
  waterBusy: boolean;
  onRelax: (isRelaxing: boolean) => Promise<boolean>;
  relaxBusy: boolean;
  onTattoo: (tattoo: ChatTattoo) => Promise<boolean>;
  tattooBusy: boolean;
  onAction: (action: ChatAction) => Promise<boolean>;
  actionBusy: boolean;
  onTrackId: () => Promise<boolean>;
  spaDoor: ChatSpaDoorState;
  spaDoorBusy: boolean;
  onSpaBouncer: (active: boolean) => Promise<boolean>;
  onSpaRequest: (message: string) => Promise<boolean>;
  onSpaDecision: (requestId: string, approved: boolean, message: string) => Promise<boolean>;
  onProfile: (sender: ChatSender) => void;
  onEditLook: () => void;
  /** Station admin. Gates the room light switch — see RoomLightSwitch. */
  isAdmin: boolean;
}) {
  const [djAction, setDjAction] = useState<DjAction>(currentDjAction);
  const [dogPets, setDogPets] = useState(0);
  const [dogExcited, setDogExcited] = useState(false);
  const [openStall, setOpenStall] = useState<number | null>(null);
  const [openLongDropCubicle, setOpenLongDropCubicle] = useState<number | null>(null);
  const [smokeOn, setSmokeOn] = useState(true);
  const [sinksOn, setSinksOn] = useState(false);
  const [mirrorFogged, setMirrorFogged] = useState(false);
  const [loungeLampOn, setLoungeLampOn] = useState(true);
  const [opticalMode, setOpticalMode] = useState(0);
  const [spaJetsOn, setSpaJetsOn] = useState(true);
  const [spaRequestOpen, setSpaRequestOpen] = useState(false);
  const [spaRequestMessage, setSpaRequestMessage] = useState('Can I come into the spa?');
  const [insideDrinkMenuOpen, setInsideDrinkMenuOpen] = useState(false);
  const [recordCratePulls, setRecordCratePulls] = useState(0);
  const [cookiesEaten, setCookiesEaten] = useState(0);
  const [roomLights, setRoomLights] = useState<Partial<Record<ChatFloorRoom, WorldLight>>>({});
  const [openParasols, setOpenParasols] = useState<Record<ParasolId, boolean>>(PARASOLS_OPEN);
  const [sundeckShower, setSundeckShower] = useState(false);
  const [sundeckScope, setSundeckScope] = useState(0);
  const [gullScatters, setGullScatters] = useState(0);
  const [trackIdBusy, setTrackIdBusy] = useState(false);
  const [trackIdCooling, setTrackIdCooling] = useState(false);
  const [roomNotice, setRoomNotice] = useState<string | null>(null);
  const trackIdTimer = useRef<number | null>(null);
  const [bubbleClock, setBubbleClock] = useState(() => Date.now());

  // A new snapshot can arrive long after the last bubble expired. Refresh the
  // clock immediately so older history never flashes back onto the floor.
  useEffect(() => setBubbleClock(Date.now()), [messages]);

  useEffect(() => {
    const nextExpiry = messages.reduce<number | null>((nearest, message) => {
      const createdAt = new Date(message.created_at).getTime();
      if (!Number.isFinite(createdAt)) return nearest;
      const expiresAt = createdAt + CHAT_BUBBLE_LIFETIME_MS;
      if (expiresAt <= bubbleClock) return nearest;
      return nearest === null || expiresAt < nearest ? expiresAt : nearest;
    }, null);
    if (nextExpiry === null) return;

    const timer = window.setTimeout(
      () => setBubbleClock(Date.now()),
      Math.max(0, nextExpiry - Date.now()) + 25,
    );
    return () => window.clearTimeout(timer);
  }, [bubbleClock, messages]);

  const bubbles = useMemo(() => {
    const latest = new Map<string, string>();
    const freshAfter = bubbleClock - CHAT_BUBBLE_LIFETIME_MS;
    for (const message of messages) {
      if (new Date(message.created_at).getTime() <= freshAfter) continue;
      latest.set(message.sender.tag, message.body);
    }
    return latest;
  }, [bubbleClock, messages]);
  const mine = me ? people.find((person) => person.tag === me.tag) : undefined;
  const currentRoom = me?.floor_room ?? HOME_ROOM;
  const visiblePeople = people.filter((person) => person.floor_room === currentRoom);
  const others = visiblePeople.filter((person) => person.tag !== me?.tag);
  const inToilets = currentRoom === 'toilets';
  const inLounge = currentRoom === 'lounge';
  const inPsychedelic = currentRoom === 'psychedelic';
  const inHamMuseum = currentRoom === 'ham-museum';
  const inTattooStudio = currentRoom === 'tattoo-studio';
  const inSpa = currentRoom === 'spa';
  const inInsideClub = currentRoom === 'inside-club';
  const inSundeck = currentRoom === 'club-sundeck';
  const inLongDrop = currentRoom === 'club-toilets';
  const inArcadeChase = currentRoom === 'arcade-chase';
  const onMainFloor = currentRoom === 'beach' || currentRoom === 'daylight' || currentRoom === 'sunset';
  const inBeachClub = currentRoom === 'club-terrace'
    || currentRoom === 'club-sundeck'
    || currentRoom === 'inside-club'
    || currentRoom === 'sunset-beach';
  const roomCopy = ROOM_COPY[currentRoom];
  const mainFloorLinks = MAIN_FLOORS.filter(
    (floor) => floor.room !== currentRoom && !HIDDEN_ROOMS.has(floor.room),
  );
  const sideRoomLinks = SIDE_ROOMS.filter((side) => !HIDDEN_ROOMS.has(side.room));
  const beachClubLinks = BEACH_CLUB_ROOMS.filter((room) => (
    room.room !== currentRoom
    && !HIDDEN_ROOMS.has(room.room)
    && !((currentRoom === 'club-terrace' || currentRoom === 'club-sundeck')
      && room.room === 'inside-club')
  ));
  const currentRoomStatus = rooms.find((room) => room.id === currentRoom);
  const roomStatus = (floorRoom: ChatFloorRoom): ChatVenueRoom | undefined => (
    rooms.find((room) => room.id === floorRoom)
  );
  const roomIsFull = (floorRoom: ChatFloorRoom): boolean => {
    const target = roomStatus(floorRoom);
    return floorRoom !== currentRoom && Boolean(target && target.occupancy >= target.capacity);
  };
  const roomDoorLabel = (floorRoom: ChatFloorRoom, label: string): string => {
    const target = roomStatus(floorRoom);
    if (!me) return 'JOIN';
    if (!target) return label;
    if (roomIsFull(floorRoom)) return 'FULL';
    if (floorRoom === 'spa' && spaDoor.bouncer && !spaDoor.bouncer.is_me) {
      return spaDoor.my_request?.status === 'pending'
        ? 'SPA · WAITING'
        : `ASK SPA ${target.occupancy}/${target.capacity}`;
    }
    return `${label} ${target.occupancy}/${target.capacity}`;
  };
  const currentDrink = BAR_DRINKS.find((drink) => drink.token === me?.drink);
  const crateFind = recordCratePulls > 0
    ? RECORD_CRATE_FINDS[(recordCratePulls - 1) % RECORD_CRATE_FINDS.length]
    : null;
  const cookieBite = cookiesEaten > 0
    ? COOKIE_JAR[(cookiesEaten - 1) % COOKIE_JAR.length]
    : null;
  // Four in the jar, one fewer each press, and empty on the press whose label
  // says it was the last one -- the jar you are looking at has to be the jar
  // the label just described. The press after that finds it filled again,
  // because somebody behind the bar keeps filling it.
  const cookiesInJar = cookiesEaten === 0
    ? COOKIE_JAR.length
    : COOKIE_JAR.length - 1 - ((cookiesEaten - 1) % COOKIE_JAR.length);
  // A room stands in the light it declares until an admin moves the hour on
  // from inside it. The override is held per room and for the session only:
  // it is a lighting desk, not a setting, and walking into a room nobody has
  // touched still shows the room as it was written.
  const roomLight: WorldLight = roomLights[currentRoom] ?? ROOM_LIGHT[currentRoom] ?? 'afternoon';
  const deckLightState = DECK_LIGHT.find((step) => step.token === roomLight) ?? DECK_LIGHT[0];
  // The way out of a room that is not a main floor and not in the club menu.
  // ROOM_EXIT names it for the rooms that do not simply return home.
  const roomExit = ROOM_EXIT[currentRoom] ?? { room: HOME_ROOM, label: 'SUNDECK' };
  const roomExitTitle = ROOM_COPY[roomExit.room].title;
  const scopeSighting = sundeckScope > 0
    ? SUNDECK_SIGHTINGS[(sundeckScope - 1) % SUNDECK_SIGHTINGS.length]
    : null;

  // Petting the sprite has to read back somehow, and the imported sheet has no
  // separate tail to wag, so the idle loop itself speeds up for a moment. It
  // settles again rather than latching, because a dog stuck at double speed for
  // the rest of the session stops looking like excitement.
  useEffect(() => {
    if (dogPets === 0) return undefined;
    setDogExcited(true);
    const timer = window.setTimeout(() => setDogExcited(false), 2400);
    return () => window.clearTimeout(timer);
  }, [dogPets]);

  useEffect(() => () => {
    if (trackIdTimer.current !== null) window.clearTimeout(trackIdTimer.current);
  }, []);

  useEffect(() => {
    const entry = spaDoor.my_request;
    if (!entry || entry.status === 'pending') return;
    if (entry.status === 'approved') {
      setRoomNotice(entry.response_message || 'The spa bouncer waved you in.');
      setSpaRequestOpen(false);
    } else if (entry.status === 'denied') {
      setRoomNotice(entry.response_message || 'Not tonight. You can ask again.');
      setSpaRequestOpen(false);
    } else if (entry.status === 'cancelled') {
      setRoomNotice(entry.response_message || 'The spa door changed. Try again.');
    }
  }, [spaDoor.my_request]);

  useEffect(() => {
    let minuteTimer: number | undefined;
    const boundaryTimer = window.setTimeout(() => {
      setDjAction(currentDjAction());
      minuteTimer = window.setInterval(() => setDjAction(currentDjAction()), 60_000);
    }, 60_050 - (Date.now() % 60_000));
    return () => {
      window.clearTimeout(boundaryTimer);
      if (minuteTimer !== undefined) window.clearInterval(minuteTimer);
    };
  }, []);

  const moveMine = (clientX: number, clientY: number, node: HTMLElement): void => {
    if (!me) return;
    const box = node.getBoundingClientRect();
    void onMove(
      ((clientX - box.left) / box.width) * 100,
      ((clientY - box.top) / box.height) * 100,
    );
  };

  const changeRoom = (floorRoom: ChatFloorRoom): void => {
    if (!me) return;
    if (roomIsFull(floorRoom)) {
      const target = roomStatus(floorRoom);
      setRoomNotice(`${target?.title ?? 'That room'} is full.`);
      return;
    }
    if (floorRoom === 'spa' && spaDoor.bouncer && !spaDoor.bouncer.is_me) {
      setRoomNotice(`${spaDoor.bouncer.name} is on the door. Send a message to ask in.`);
      setSpaRequestOpen(true);
      return;
    }
    setRoomNotice(null);
    void onMove(ROOM_ARRIVAL_X[floorRoom], 72, floorRoom).then((moved) => {
      if (!moved) setRoomNotice('No space right now. Try another room.');
    });
  };

  // Next hour in the cycle, starting from whatever the room is showing now, so
  // the first press moves the terrace on from golden rather than snapping it
  // back to the top of the list.
  const cycleRoomLight = (): void => {
    const step = DECK_LIGHT.findIndex((item) => item.token === roomLight);
    const next = DECK_LIGHT[(step + 1) % DECK_LIGHT.length].token;
    setRoomLights((current) => ({ ...current, [currentRoom]: next }));
  };

  const toggleParasol = (id: ParasolId): void => {
    setOpenParasols((open) => ({ ...open, [id]: !open[id] }));
  };

  const askTrackId = (): void => {
    if (!me || trackIdBusy || trackIdCooling) return;
    setTrackIdBusy(true);
    void onTrackId()
      .then((sent) => {
        if (!sent) return;
        setRoomNotice('Track ID request posted in the main room chat.');
        setTrackIdCooling(true);
        trackIdTimer.current = window.setTimeout(() => {
          setTrackIdCooling(false);
          trackIdTimer.current = null;
        }, 15_000);
      })
      .finally(() => setTrackIdBusy(false));
  };

  return (
    <section
      className="chat-stage"
      data-room={currentRoom}
      data-light={roomLight}
      aria-labelledby="chat-stage-title"
    >
      <div className="chat-stage__bar">
        <div className="chat-stage__identity">
          <span className="chat-stage__eyebrow mono">
            {roomCopy.eyebrow}
          </span>
          <strong id="chat-stage-title">{roomCopy.title}</strong>
        </div>
        <div className="chat-stage__presence mono">
          <i aria-hidden="true" />
          {currentRoomStatus?.occupancy ?? (visiblePeople.length || (me ? 1 : 0))}
          /{currentRoomStatus?.capacity ?? 20} {roomCopy.countLabel}
        </div>
        {me ? (
          <button type="button" className="chat-stage__editlook mono" onClick={onEditLook}>
            Edit look
          </button>
        ) : null}
        {me ? (
          <label className="chat-stage__action mono">
            <span>Action</span>
            <select
              value={me.action}
              disabled={actionBusy}
              onChange={(event) => void onAction(event.target.value as ChatAction)}
              aria-label="Choose an avatar action"
            >
              {ACTIONS.map((item) => (
                <option value={item.token} key={item.token}>{item.label}</option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
      <div
        className="chat-floor"
        data-room={currentRoom}
        data-smoke={smokeOn ? 'on' : 'off'}
        data-sinks={sinksOn ? 'on' : 'off'}
        data-mirror={mirrorFogged ? 'fogged' : 'clear'}
        data-lounge-lamp={loungeLampOn ? 'on' : 'off'}
        data-optical-mode={opticalMode}
        data-light={roomLight}
        data-sundeck-parasol={openParasols.sundeck ? 'open' : 'down'}
        data-sundeck-shower={sundeckShower ? 'on' : 'off'}
        role="application"
        aria-label={roomCopy.ariaLabel}
        tabIndex={0}
        onClick={(event) => {
          if ((event.target as HTMLElement).closest('button, input, .chat-floor__person, .chat-spa-entry, .chat-spa-door')) return;
          moveMine(event.clientX, event.clientY, event.currentTarget);
        }}
        onKeyDown={(event) => {
          if (!me || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
          event.preventDefault();
          void onMove(
            (mine?.x ?? 50) + (event.key === 'ArrowLeft' ? -4 : event.key === 'ArrowRight' ? 4 : 0),
            (mine?.y ?? 72) + (event.key === 'ArrowUp' ? -4 : event.key === 'ArrowDown' ? 4 : 0),
          );
        }}
      >
        {roomNotice ? (
          <div className="chat-floor__roomnotice mono" role="status">{roomNotice}</div>
        ) : null}
        {inArcadeChase && me ? <ArcadeChase playerName={me.display_name} /> : null}
        {spaRequestOpen && !inSpa ? (
          <form
            className="chat-spa-entry"
            onSubmit={(event) => {
              event.preventDefault();
              void onSpaRequest(spaRequestMessage).then((sent) => {
                if (sent) {
                  setRoomNotice('Request sent. The spa bouncer will answer here.');
                  setSpaRequestOpen(false);
                }
              });
            }}
          >
            <span className="mono">NIGHT SPA DOOR</span>
            <strong>Ask {spaDoor.bouncer?.name ?? 'the bouncer'} to come in</strong>
            <input
              value={spaRequestMessage}
              maxLength={120}
              autoFocus
              aria-label="Message for the spa bouncer"
              onChange={(event) => setSpaRequestMessage(event.target.value)}
            />
            <div>
              <button type="submit" disabled={spaDoorBusy || !spaRequestMessage.trim()}>
                {spaDoorBusy ? 'Sending…' : 'Ask to enter'}
              </button>
              <button type="button" disabled={spaDoorBusy} onClick={() => setSpaRequestOpen(false)}>
                Cancel
              </button>
            </div>
          </form>
        ) : null}
        <div className="chat-floor__wall" aria-hidden="true">
          <div className="chat-floor__neon"><span>HIGHER</span><span>GROUND</span><small>EXPERIENCE</small></div>
          <div className="chat-floor__moon"><i /></div>
          <div className="chat-floor__sky-events">
            <span className="chat-floor__shooting-star" />
            <span className="chat-floor__birds"><i /><i /><i /></span>
          </div>
          <div className="chat-floor__horizon">
            <span /><span /><span /><span /><span /><span />
            <b className="chat-floor__lighthouse"><i /></b>
          </div>
          <div className="chat-floor__ocean">
            <i /><i /><i />
            <span className="chat-floor__boat"><b /><em /></span>
          </div>
          <div className="chat-floor__road">
            <span className="chat-floor__road-line" />
            <span className="chat-floor__vehicle chat-floor__vehicle--car"><i /><b /></span>
            <span className="chat-floor__vehicle chat-floor__vehicle--van"><i /><b /></span>
            <span className="chat-floor__cyclist"><i /><b /></span>
            <span className="chat-floor__wrong-taxi"><i /><b /><em>WRONG TAXI</em></span>
          </div>
        </div>
        <div className="chat-floor__toilets">
          <span className="chat-toilets__tube"><i /></span>
          <span className="chat-toilets__graffiti">HGR<br />WAS<br />HERE</span>
          <button
            type="button"
            className="chat-toilets__mirror"
            onClick={() => setMirrorFogged((value) => !value)}
            aria-label={mirrorFogged ? 'Clear the fogged mirror' : 'Fog up the mirror'}
          ><i /><i /><i /><b className="mono">{mirrorFogged ? 'WIPE' : 'FOG'}</b></button>
          <button
            type="button"
            className="chat-toilets__sinks"
            onClick={() => setSinksOn((value) => !value)}
            aria-label={sinksOn ? 'Turn the taps off' : 'Turn the taps on'}
          ><i /><i /><b /><b /><em className="mono">{sinksOn ? 'TAPS ON' : 'TAPS OFF'}</em></button>
          <div className="chat-toilets__stalls">
            {[1, 2, 3].map((stall) => {
              const isOpen = openStall === stall;
              return (
                <button
                  type="button"
                  className="chat-toilets__stall"
                  data-open={isOpen ? 'true' : undefined}
                  onClick={() => setOpenStall(isOpen ? null : stall)}
                  aria-expanded={isOpen}
                  aria-label={`${isOpen ? 'Close' : 'Open'} toilet stall ${stall}`}
                  key={stall}
                >
                  <i aria-hidden="true"><b>{stall}</b></i>
                  <span aria-hidden="true"><b /></span>
                </button>
              );
            })}
          </div>
          <span className="chat-toilets__dryer"><i /></span>
          <span className="chat-toilets__bucket"><i /></span>
          <span className="chat-toilets__floor-sign">MIND THE PUDDLE</span>
        </div>
        <div className="chat-floor__lounge">
          <span className="chat-lounge__ceiling"><i /><i /></span>
          <span className="chat-lounge__poster">SIT<br />DOWN<br />BREATHE</span>
          <span className="chat-lounge__balloons">
            <i /><i /><i /><i /><i /><i /><i /><i />
          </span>
          <span className="chat-lounge__sofa chat-lounge__sofa--left"><i /><i /></span>
          <span className="chat-lounge__sofa chat-lounge__sofa--back"><i /><i /></span>
          <span className="chat-lounge__sofa chat-lounge__sofa--right"><i /><i /></span>
          <span className="chat-lounge__table"><i /><i /><b /></span>
          <button
            type="button"
            className="chat-lounge__lamp"
            onClick={() => setLoungeLampOn((value) => !value)}
            aria-label={loungeLampOn ? 'Dim the lounge lamp' : 'Turn the lounge lamp on'}
          ><i /><b className="mono">{loungeLampOn ? 'DIM' : 'LIGHT'}</b></button>
          <span className="chat-lounge__plant"><i /><i /><i /></span>
          <span className="chat-lounge__fan"><i /><i /><i /></span>
        </div>
        <div className="chat-floor__psychedelic">
          <span className="chat-optical__ceiling"><i /><i /><i /><i /><i /></span>
          <span className="chat-optical__tunnel"><i /><i /><i /><i /><i /><i /></span>
          <span className="chat-optical__blob chat-optical__blob--left" />
          <span className="chat-optical__blob chat-optical__blob--right" />
          <span className="chat-optical__prism"><i /><i /><i /></span>
          <span className="chat-optical__seat chat-optical__seat--left" />
          <span className="chat-optical__seat chat-optical__seat--right" />
          <button
            type="button"
            className="chat-optical__switch mono"
            onClick={() => setOpticalMode((value) => (value + 1) % 3)}
            aria-label="Change the optical room pattern"
          >PATTERN {opticalMode + 1}/3</button>
        </div>
        <div className="chat-floor__ham-museum" aria-hidden="true">
          <span className="chat-ham__awning">MUSEO DEL JAMÓN</span>
          <span className="chat-ham__hanging"><i /><i /><i /><i /></span>
          <span className="chat-ham__tiles" />
          <span className="chat-ham__counter"><i /><i /><b /></span>
          <span className="chat-ham__cutter"><i /><b /><em /><strong /></span>
          <span className="chat-ham__plate"><i /><i /><i /><i /><i /></span>
          <span className="chat-ham__sign mono">CORTE FINO · 24/7</span>
        </div>
        <div className="chat-floor__tattoo-studio">
          <span className="chat-tattoo__neon">NEEDLE<br />&amp; SIGNAL</span>
          <span className="chat-tattoo__flash"><i>H</i><i>✦</i><i>≋</i><i>♥</i></span>
          <span className="chat-tattoo__chair"><i /><b /></span>
          <span className="chat-tattoo__artist"><i /><b /><em /></span>
          <span className="chat-tattoo__lamp"><i /></span>
          <div className="chat-tattoo__catalogue" aria-label="Choose a profile tattoo">
            {TATTOOS.map((item) => (
              <button
                type="button"
                className="mono"
                data-selected={me?.tattoo === item.token ? 'true' : undefined}
                disabled={!me || tattooBusy}
                onClick={() => void onTattoo(item.token)}
                aria-label={`Choose ${item.label} tattoo`}
                key={item.token}
              >
                <b aria-hidden="true">{item.glyph}</b><span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="chat-floor__spa" data-jets={spaJetsOn ? 'on' : 'off'}>
          <span className="chat-spa__tiles" />
          <span className="chat-spa__steam"><i /><i /><i /><i /><i /></span>
          <span className="chat-spa__djbooth">
            <span className="chat-spa__dj"><i /><b /><em /><strong /></span>
            <span className="chat-spa__decks"><i /><i /><b /></span>
          </span>
          <span className="chat-spa__pool"><i /><i /><i /><i /><i /><i /></span>
          <span className="chat-spa__float chat-spa__float--ring" />
          <span className="chat-spa__float chat-spa__float--duck"><i /></span>
          <span className="chat-spa__float chat-spa__float--ball" />
          <span className="chat-spa__lounger"><i /><b /></span>
          <button
            type="button"
            className="chat-spa__jets mono"
            onClick={() => setSpaJetsOn((value) => !value)}
            aria-pressed={spaJetsOn}
          >{spaJetsOn ? 'BUBBLES ON' : 'BUBBLES OFF'}</button>
          {inSpa && me ? (
            <SpaDoorControls
              door={spaDoor}
              busy={spaDoorBusy}
              onBouncer={onSpaBouncer}
              onDecision={onSpaDecision}
            />
          ) : null}
        </div>
        <div className="chat-floor__club-terrace" aria-label="Interactive beach club terrace">
          <span className="chat-club__sky"><i /><i /><i /></span>
          <span className="chat-club__ocean"><i /><i /><i /></span>
          <span className="chat-terrace__beach" aria-hidden="true"><i /><i /><i /><i /><i /></span>
          <button
            type="button"
            className="chat-terrace__clubhouse"
            disabled={!me || roomIsFull('inside-club')}
            onClick={() => changeRoom('inside-club')}
            aria-label={roomIsFull('inside-club')
              ? 'The HGE Beach Club is full'
              : 'Open the clubhouse door and enter the HGE Beach Club'}
          >
            <i className="chat-terrace__roof" />
            <b className="chat-terrace__sign">HIGHER GROUND<br />EXPERIENCE</b>
            <span className="chat-terrace__windows"><i /><i /></span>
            <span className="chat-terrace__door"><i />ENTER</span>
          </button>
          <span className="chat-terrace__container">HGE<br />BEACH</span>
          <span className="chat-terrace__railing"><i /><i /><i /><i /><i /></span>
          <span className="chat-terrace__lights"><i /><i /><i /><i /><i /><i /></span>
          <span className="chat-terrace__table chat-terrace__table--left"><i /><b /><em /></span>
          <span className="chat-terrace__table chat-terrace__table--right"><i /><b /><em /></span>
          <span className="chat-terrace__bench chat-terrace__bench--left"><i /><b /><em /></span>
          <span className="chat-terrace__bench chat-terrace__bench--right"><i /><b /><em /></span>
          {/* The two sponsored umbrellas crank down like the sundeck's parasol.
              They keep the brand on the canopy, so the label that says what the
              next press does is a third element rather than the <b>. */}
          {TERRACE_UMBRELLAS.map((umbrella) => (
            <button
              type="button"
              className={`chat-terrace__umbrella chat-terrace__umbrella--${umbrella.modifier}`}
              data-open={openParasols[umbrella.id] ? 'true' : undefined}
              onClick={() => toggleParasol(umbrella.id)}
              aria-pressed={openParasols[umbrella.id]}
              aria-label={openParasols[umbrella.id]
                ? `Close the ${umbrella.brand} terrace umbrella`
                : `Open the ${umbrella.brand} terrace umbrella`}
              key={umbrella.id}
            >
              <i /><b>{umbrella.brand}</b><em />
              <span className="mono">{openParasols[umbrella.id] ? 'PARASOL OPEN' : 'PARASOL DOWN'}</span>
            </button>
          ))}
          <span className="chat-terrace__plant chat-terrace__plant--left"><i /><i /><i /></span>
          <span className="chat-terrace__plant chat-terrace__plant--right"><i /><i /><i /></span>
        </div>
        <div className="chat-floor__club-sundeck" aria-label="Interactive beach club sundeck terrace">
          <span className="chat-sundeck__sky" aria-hidden="true">
            <b className="chat-sundeck__sun" />
            <i className="chat-sundeck__cloud chat-sundeck__cloud--far" />
            <i className="chat-sundeck__cloud chat-sundeck__cloud--mid" />
            <i className="chat-sundeck__cloud chat-sundeck__cloud--near" />
            <em className="chat-sundeck__haze" />
          </span>
          <span className="chat-sundeck__headland" aria-hidden="true">
            <i /><i /><i /><b /><b /><b />
          </span>
          <span className="chat-sundeck__ocean" aria-hidden="true">
            <i /><i /><i /><i />
            <b className="chat-sundeck__glitter" />
            <em className="chat-sundeck__boat"><i /><b /></em>
          </span>
          <span className="chat-sundeck__shore" aria-hidden="true"><i /><i /><i /></span>
          <span className="chat-sundeck__sand" aria-hidden="true">
            <i /><i /><i /><i /><i /><i />
            <b className="chat-sundeck__grass chat-sundeck__grass--left"><em /><em /><em /></b>
            <b className="chat-sundeck__grass chat-sundeck__grass--right"><em /><em /><em /></b>
            <b className="chat-sundeck__skiff"><em /></b>
          </span>
          <span className="chat-sundeck__apron" aria-hidden="true"><i /><b /></span>
          <span className="chat-sundeck__shaft" aria-hidden="true" />
          <span className="chat-sundeck__glowpool" aria-hidden="true"><i /><i /><i /><i /></span>
          <button
            type="button"
            className="chat-sundeck__clubhouse"
            disabled={!me || roomIsFull('inside-club')}
            onClick={() => changeRoom('inside-club')}
            aria-label={roomIsFull('inside-club')
              ? 'The HGE Beach Club is full'
              : 'Open the clubhouse door and enter the HGE Beach Club'}
          >
            <i className="chat-sundeck__roof"><em /><em /><em /></i>
            <b className="chat-sundeck__sign">HIGHER GROUND<br />EXPERIENCE</b>
            <span className="chat-sundeck__windows"><i /><i /></span>
            <span className="chat-sundeck__board">TONIGHT<br />SUNDOWN SET</span>
            <span className="chat-sundeck__door"><i />ENTER</span>
          </button>
          <span className="chat-sundeck__pergola" aria-hidden="true">
            <i /><i />
            <b className="chat-sundeck__post chat-sundeck__post--left" />
            <b className="chat-sundeck__post chat-sundeck__post--right" />
          </span>
          <span className="chat-sundeck__bunting" aria-hidden="true">
            <i /><i /><i /><i /><i /><i /><i /><i /><i /><i />
          </span>
          <span className="chat-sundeck__festoon" aria-hidden="true">
            <i /><i /><i /><i /><i /><i /><i /><i />
          </span>
          <span className="chat-sundeck__rail" aria-hidden="true">
            <i /><i /><i /><i /><i />
            <b className="chat-sundeck__ring" />
            <b className="chat-sundeck__towel chat-sundeck__towel--one" />
            <b className="chat-sundeck__towel chat-sundeck__towel--two" />
          </span>
          <StationDjBooth action={djAction} brand="HGE" terrace />
          <button
            type="button"
            className="chat-sundeck__trackid mono"
            disabled={!me || trackIdBusy || trackIdCooling}
            onClick={askTrackId}
            aria-label="Ask for the current track ID in the main room chat"
          >
            <i aria-hidden="true">?</i>
            <b>{trackIdBusy ? 'SENDING…' : trackIdCooling ? 'ASKED ✓' : 'ASK TRACK ID'}</b>
          </button>
          <span className="chat-sundeck__stack chat-sundeck__stack--left" aria-hidden="true"><i /><i /><b /></span>
          <span className="chat-sundeck__stack chat-sundeck__stack--right" aria-hidden="true"><i /><i /><b /></span>
          <span className="chat-sundeck__bar" aria-hidden="true">
            <i className="chat-sundeck__bartop" />
            <b className="chat-sundeck__bottles"><em /><em /><em /><em /></b>
            <b className="chat-sundeck__stool chat-sundeck__stool--one" />
            <b className="chat-sundeck__stool chat-sundeck__stool--two" />
          </span>
          <span className="chat-sundeck__table" aria-hidden="true">
            <i /><b />
            <em className="chat-sundeck__bucket"><b /><b /></em>
            <em className="chat-sundeck__glass chat-sundeck__glass--one" />
            <em className="chat-sundeck__glass chat-sundeck__glass--two" />
          </span>
          <span className="chat-sundeck__lounger chat-sundeck__lounger--one" aria-hidden="true"><i /><b /><em /></span>
          <span className="chat-sundeck__lounger chat-sundeck__lounger--two" aria-hidden="true"><i /><b /><em /></span>
          <span className="chat-sundeck__planter chat-sundeck__planter--left" aria-hidden="true"><i /><i /><i /><i /></span>
          <span className="chat-sundeck__planter chat-sundeck__planter--right" aria-hidden="true"><i /><i /><i /><i /></span>
          <span className="chat-sundeck__crates" aria-hidden="true"><i /><i /><b /></span>
          <span className="chat-sundeck__grill" aria-hidden="true"><i /><b /><em /><em /><em /></span>
          <button
            type="button"
            className="chat-sundeck__parasol"
            data-open={openParasols.sundeck ? 'true' : undefined}
            onClick={() => toggleParasol('sundeck')}
            aria-pressed={openParasols.sundeck}
            aria-label={openParasols.sundeck ? 'Close the sundeck parasol' : 'Open the sundeck parasol'}
          >
            <i /><em />
            <b className="mono">{openParasols.sundeck ? 'PARASOL OPEN' : 'PARASOL DOWN'}</b>
          </button>
          <button
            type="button"
            className="chat-sundeck__daybed"
            disabled={!me || relaxBusy}
            onClick={() => void onRelax(!me?.is_relaxing)}
            aria-label={me?.is_relaxing ? 'Get up from the daybed' : 'Lie down on the daybed'}
          >
            <i /><em /><em />
            <b className="mono">{relaxBusy ? 'ONE SEC…' : me?.is_relaxing ? 'GET UP' : 'TAKE THE DAYBED'}</b>
          </button>
          <button
            type="button"
            className="chat-sundeck__shower"
            data-on={sundeckShower ? 'true' : undefined}
            onClick={() => setSundeckShower((on) => !on)}
            aria-pressed={sundeckShower}
            aria-label={sundeckShower ? 'Turn the deck shower off' : 'Turn the deck shower on'}
          >
            <i /><em><span /><span /><span /><span /></em>
            <b className="mono">{sundeckShower ? 'RINSE ON' : 'RINSE OFF'}</b>
          </button>
          <button
            type="button"
            className="chat-sundeck__scope"
            onClick={() => setSundeckScope((looks) => looks + 1)}
            aria-label="Look through the deck telescope"
          >
            <i /><em />
            <b className="mono">{scopeSighting ?? 'USE THE SCOPE'}</b>
          </button>
          <button
            type="button"
            className="chat-sundeck__gulls"
            onClick={() => setGullScatters((count) => count + 1)}
            aria-label={gullScatters > 0
              ? `Scatter the gulls again. Scattered ${gullScatters} times.`
              : 'Scatter the gulls off the rail'}
          >
            <span className="chat-sundeck__flock" key={gullScatters}><i /><i /><i /><i /><i /></span>
            <b className="mono">{gullScatters > 0 ? `SCATTERED ×${gullScatters}` : 'SCATTER THE GULLS'}</b>
          </button>
          <span className="chat-sundeck__motes" aria-hidden="true"><i /><i /><i /><i /><i /><i /></span>
        </div>
        <div className="chat-floor__inside-club" aria-label="Interactive indoor bar and chill room">
          <span className="chat-inside__window" aria-hidden="true"><i /><i /><i /></span>
          <span className="chat-inside__rafters" aria-hidden="true"><i /><i /><i /><i /><i /></span>
          <span className="chat-inside__globes" aria-hidden="true"><i /><i /><i /><i /><i /><i /><i /></span>
          <div className="chat-inside__bar" aria-hidden="true">
            <b>LOW TIDE BAR</b><i /><i /><i /><i /><em /><strong />
            <span className="chat-inside__bartender"><i /><b /><em /></span>
            <span className="chat-inside__barback"><i /><i /><i /><i /><i /><i /></span>
          </div>
          <span className="chat-inside__sofa chat-inside__sofa--left" aria-hidden="true"><i /><i /><b /></span>
          <span className="chat-inside__sofa chat-inside__sofa--back" aria-hidden="true"><i /><i /><b /></span>
          <span className="chat-inside__sofa chat-inside__sofa--right" aria-hidden="true"><i /><i /><b /></span>
          <span className="chat-inside__lowtable chat-inside__lowtable--left" aria-hidden="true"><i /><b /></span>
          <span className="chat-inside__lowtable chat-inside__lowtable--right" aria-hidden="true"><i /><b /></span>
          {/* A thing standing on a table rather than a fourth mint button, so
              its label goes above the lid -- there is a tabletop below it. */}
          <button
            type="button"
            className="chat-inside__cookies"
            data-in-jar={cookiesInJar}
            onClick={() => setCookiesEaten((eaten) => eaten + 1)}
            aria-label={cookiesEaten > 0
              ? `Take another cookie from the jar. Eaten ${cookiesEaten}.`
              : 'Take a cookie from the jar'}
          >
            <i aria-hidden="true"><b /><b /><b /><b /></i>
            <em aria-hidden="true" />
            <span className="mono">{cookieBite ?? 'TAKE A COOKIE'}</span>
          </button>
          <span className="chat-inside__floorlights" aria-hidden="true"><i /><i /><i /><i /><i /><i /><i /><i /></span>
          {/* The only way into The Long Drop. It is drawn here, in this room's
              scene, rather than added to BEACH_CLUB_ROOMS -- which is what
              makes the room reachable from the bar and from nowhere else. */}
          <button
            type="button"
            className="chat-inside__wc"
            disabled={!me || roomIsFull('club-toilets')}
            onClick={() => changeRoom('club-toilets')}
            aria-label={roomIsFull('club-toilets')
              ? 'The Long Drop is full'
              : 'Go out the back to The Long Drop'}
          >
            <i className="chat-inside__wc-frame" aria-hidden="true" />
            <b className="chat-inside__wc-sign">WC</b>
            <span className="chat-inside__wc-arrow" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="chat-inside__order mono"
            disabled={!me || waterBusy}
            aria-expanded={insideDrinkMenuOpen}
            onClick={() => setInsideDrinkMenuOpen((open) => !open)}
          >
            {waterBusy ? 'ONE SEC…' : currentDrink ? `${currentDrink.short} · CHANGE` : 'ORDER A DRINK'}
          </button>
          {insideDrinkMenuOpen ? (
            <div className="chat-inside__drinkmenu" role="group" aria-label="Choose a drink">
              <span className="mono">WHAT ARE YOU HAVING?</span>
              {BAR_DRINKS.map((drink) => (
                <button
                  type="button"
                  className="mono"
                  data-drink={drink.token}
                  data-selected={me?.drink === drink.token ? 'true' : undefined}
                  disabled={!me || waterBusy}
                  onClick={() => void onDrink(drink.token).then((served) => {
                    if (served) setInsideDrinkMenuOpen(false);
                  })}
                  key={drink.token}
                >
                  <i aria-hidden="true" />{drink.label}
                </button>
              ))}
              {me?.drink !== 'none' ? (
                <button
                  type="button"
                  className="mono chat-inside__finish"
                  disabled={waterBusy}
                  onClick={() => void onDrink('none').then((finished) => {
                    if (finished) setInsideDrinkMenuOpen(false);
                  })}
                >FINISH DRINK</button>
              ) : null}
            </div>
          ) : null}
          <button
            type="button"
            className="chat-inside__sit mono"
            disabled={!me || relaxBusy}
            onClick={() => void onRelax(!me?.is_relaxing)}
          >
            {relaxBusy ? 'ONE SEC…' : me?.is_relaxing ? 'STAND UP' : 'SIT ON A SOFA'}
          </button>
          <div className="chat-inside__crate mono">
            <button
              type="button"
              className="chat-inside__crate-pull"
              aria-label={crateFind
                ? `Pull another record; ${crateFind.sleeve} is selected`
                : 'Flip through records'}
              onClick={() => setRecordCratePulls((pulls) => pulls + 1)}
            >
              <i aria-hidden="true"><b /><b /><b /></i>
              {crateFind ? (
                <span><b>{crateFind.sleeve}</b><em>{crateFind.note}</em></span>
              ) : (
                <span>FLIP THROUGH RECORDS</span>
              )}
            </button>
            {crateFind ? (
              <a
                className="chat-inside__crate-link"
                href={crateFind.releaseUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${crateFind.sleeve} on ${crateFind.releaseSource} in a new tab`}
              >
                {crateFind.releaseSource} ↗
              </a>
            ) : null}
          </div>
        </div>
        {/* Out the back of the club: a corrugated toilet block with a bar in
            it. Same visual language as the rest of the beach club -- blocky
            timber, warm glass, a stepped sky -- but every colour comes from the
            lanterns ramp, so the tin, the sand and the sea are all lit by the
            same string of bulbs. */}
        <div
          className="chat-floor__club-toilets"
          aria-label="Interactive toilet block bar behind the beach club"
        >
          <span className="chat-longdrop__sky" aria-hidden="true"><i /><i /><i /></span>
          <span className="chat-longdrop__sea" aria-hidden="true">
            <i /><i /><i />
            <b className="chat-longdrop__glitter" />
          </span>
          <span className="chat-longdrop__screen chat-longdrop__screen--left" aria-hidden="true">
            <i /><i /><i /><i /><i /><i />
          </span>
          <span className="chat-longdrop__screen chat-longdrop__screen--right" aria-hidden="true">
            <i /><i /><i /><i /><i /><i />
          </span>
          <span className="chat-longdrop__roof" aria-hidden="true">
            <i /><i /><i /><i /><i /><i /><i /><i /><i /><i />
            <b className="chat-longdrop__gutter" />
          </span>
          <div className="chat-longdrop__block">
            <b className="chat-longdrop__sign" aria-hidden="true">WC</b>
            {[1, 2].map((cubicle) => {
              const isOpen = openLongDropCubicle === cubicle;
              const isOccupied = cubicle === 2;
              return (
                <button
                  type="button"
                  className={`chat-longdrop__cubicle chat-longdrop__cubicle--${cubicle === 1 ? 'left' : 'right'}`}
                  data-open={isOpen ? 'true' : undefined}
                  data-occupied={isOccupied ? 'true' : undefined}
                  aria-expanded={isOpen}
                  aria-label={isOpen
                    ? `Close toilet door ${cubicle}${isOccupied ? '; someone is inside' : ''}`
                    : `Open toilet door ${cubicle}`}
                  onClick={() => setOpenLongDropCubicle(isOpen ? null : cubicle)}
                  key={cubicle}
                >
                  {isOccupied ? (
                    <span className="chat-longdrop__occupant" aria-hidden="true"><i /><b /><em /></span>
                  ) : null}
                  <span className="chat-longdrop__door" aria-hidden="true"><em /></span>
                </button>
              );
            })}
            <span className="chat-longdrop__queue" aria-hidden="true"><i /><i /></span>
          </div>
          <div className="chat-longdrop__bar" aria-hidden="true">
            <b className="chat-longdrop__board">THE LONG DROP</b>
            <i className="chat-longdrop__deck" />
            <span className="chat-longdrop__drum chat-longdrop__drum--left"><i /><i /></span>
            <span className="chat-longdrop__drum chat-longdrop__drum--right"><i /><i /></span>
            <span className="chat-longdrop__barkeep"><i /><b /><em /></span>
            <span className="chat-longdrop__bottles"><i /><i /><i /><i /><i /><i /></span>
            <span className="chat-longdrop__limes"><i /><i /><i /><i /></span>
          </div>
          <span className="chat-longdrop__lights" aria-hidden="true">
            <i /><i /><i /><i /><i /><i /><i /><i />
          </span>
          <span className="chat-longdrop__bucket" aria-hidden="true"><i /><b /></span>
          <span className="chat-longdrop__mop" aria-hidden="true"><i /><b /></span>
          <span className="chat-longdrop__puddle" aria-hidden="true"><i /><i /></span>
          <span className="chat-longdrop__sand" aria-hidden="true"><i /><i /><i /><i /></span>
        </div>
        {/* The scenery is decorative and the parasol is not, so aria-hidden sits
            on each piece of scenery rather than on the room: a focusable
            control inside an aria-hidden subtree is unreachable. */}
        <div className="chat-floor__sunset-beach" aria-label="Interactive sunset beach and seating area">
          <span className="chat-sunsetbeach__sky" aria-hidden="true"><i /><i /><i /></span>
          <span className="chat-sunsetbeach__sun" aria-hidden="true" />
          <span className="chat-sunsetbeach__ocean" aria-hidden="true"><i /><i /><i /><i /></span>
          <span className="chat-sunsetbeach__shore" aria-hidden="true"><i /><i /><i /></span>
          <span className="chat-sunsetbeach__club" aria-hidden="true"><i /><b>HGE</b><em /></span>
          <span className="chat-sunsetbeach__boardwalk" aria-hidden="true"><i /><i /><i /></span>
          <button
            type="button"
            className="chat-sunsetbeach__parasol"
            data-open={openParasols['sunset-beach'] ? 'true' : undefined}
            onClick={() => toggleParasol('sunset-beach')}
            aria-pressed={openParasols['sunset-beach']}
            aria-label={openParasols['sunset-beach']
              ? 'Close the beach parasol'
              : 'Open the beach parasol'}
          >
            <i /><b />
            <span className="mono">{openParasols['sunset-beach'] ? 'PARASOL OPEN' : 'PARASOL DOWN'}</span>
          </button>
          <span className="chat-sunsetbeach__seat chat-sunsetbeach__seat--one" aria-hidden="true"><i /><b /></span>
          <span className="chat-sunsetbeach__seat chat-sunsetbeach__seat--two" aria-hidden="true"><i /><b /></span>
          <span className="chat-sunsetbeach__seat chat-sunsetbeach__seat--three" aria-hidden="true"><i /><b /></span>
          <span className="chat-sunsetbeach__bench chat-sunsetbeach__bench--left" aria-hidden="true"><i /><b /><em /></span>
          <span className="chat-sunsetbeach__bench chat-sunsetbeach__bench--right" aria-hidden="true"><i /><b /><em /></span>
          <span className="chat-sunsetbeach__lowtable" aria-hidden="true"><i /><i /><b /></span>
          <span className="chat-sunsetbeach__dunes" aria-hidden="true"><i /><i /><i /><i /><i /></span>
        </div>
        {isAdmin && LIT_ROOMS.has(currentRoom) ? (
          <RoomLightSwitch light={deckLightState} onCycle={cycleRoomLight} />
        ) : null}
        <div className="chat-floor__speaker chat-floor__speaker--left" aria-hidden="true"><i /><i /></div>
        <div className="chat-floor__speaker chat-floor__speaker--right" aria-hidden="true"><i /><i /></div>
        <StationDjBooth action={djAction} />
        <div className="chat-floor__bar">
          <span className="chat-floor__bar-sign">LOW TIDE</span>
          <span className="chat-floor__bartender">
            <i /><b /><em />
          </span>
          <span className="chat-floor__bar-counter"><i /><i /></span>
          <span className="chat-floor__bar-dancer">
            <i /><b /><em />
          </span>
          <button
            type="button"
            className="chat-floor__water mono"
            disabled={!me || waterBusy}
            onClick={() => void onWater(!me?.has_water)}
          >
            {waterBusy ? 'ONE SEC…' : me?.has_water ? 'DRINK WATER' : 'ORDER WATER'}
          </button>
        </div>
        <div className="chat-floor__generator" aria-hidden="true">
          <span>POWER?</span><i /><b /><em />
        </div>
        <div className="chat-floor__record-dealer" aria-hidden="true">
          <span>WHITE LABELS</span>
          <i><b /></i>
          <em><b /><b /><b /></em>
        </div>
        <div className="chat-floor__track-detective" aria-hidden="true">
          <span>TRACK ID?</span>
          <i><b /></i>
          <em />
        </div>
        <div className="chat-floor__delivery-rider" aria-hidden="true">
          <span>WHO ORDERED?</span>
          <i><b /></i>
          <em><b /><b /></em>
        </div>
        <div className="chat-floor__palm" aria-hidden="true"><b /><i /><i /><i /><i /><i /></div>
        <button
          type="button"
          className="chat-floor__dog"
          data-petted={dogPets > 0 ? 'true' : undefined}
          data-excited={dogExcited ? 'true' : undefined}
          onClick={() => setDogPets((value) => value + 1)}
          aria-label={dogPets > 0 ? `Pet the beach dog again. Petted ${dogPets} times.` : 'Pet the beach dog'}
        >
          <span className="chat-dog__bubble mono">{dogPets > 0 ? `WOOF ×${dogPets}` : 'PET ME'}</span>
          <span className="chat-dog__sprite" aria-hidden="true" />
          <span className="chat-dog__walk" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="chat-floor__smoke mono"
          data-active={smokeOn ? 'true' : undefined}
          onClick={() => setSmokeOn((value) => !value)}
          aria-pressed={smokeOn}
          aria-label={smokeOn ? 'Turn the smoke machine off' : 'Turn the smoke machine on'}
        >
          <i aria-hidden="true"><b /><b /><b /></i>
          <span>{smokeOn ? 'SMOKE ON' : 'SMOKE OFF'}</span>
        </button>
        {inLounge ? (
          <button
            type="button"
            className="chat-floor__relax mono"
            disabled={!me || relaxBusy}
            onClick={() => void onRelax(!me?.is_relaxing)}
          >
            {relaxBusy ? 'ONE SEC…' : me?.is_relaxing ? 'STAND UP' : 'SIT & RELAX'}
          </button>
        ) : null}
        {onMainFloor ? (
          <>
            {mainFloorLinks.map((floor, index) => (
              <button
                type="button"
                className="chat-floor__portal mono"
                data-destination={floor.room}
                data-menu-slot={index + 1}
                disabled={!me || roomIsFull(floor.room)}
                onClick={() => changeRoom(floor.room)}
                aria-label={roomIsFull(floor.room) ? `${floor.name} is full` : `Enter ${floor.name}`}
                key={floor.room}
              >
                <i aria-hidden="true" />
                <span>{roomDoorLabel(floor.room, floor.label)}</span>
              </button>
            ))}
            {sideRoomLinks.map((side, index) => (
              <button
                type="button"
                className={side.modifier
                  ? `chat-floor__portal chat-floor__portal--${side.modifier} mono`
                  : 'chat-floor__portal mono'}
                data-destination={side.room}
                data-menu-slot={mainFloorLinks.length + index + 1}
                disabled={!me || roomIsFull(side.room)}
                onClick={() => changeRoom(side.room)}
                aria-label={roomIsFull(side.room)
                  ? side.fullLabel
                  : side.room === 'spa' && spaDoor.bouncer
                    ? `Ask ${spaDoor.bouncer.name} for entry to the night spa`
                    : side.enterLabel}
                key={side.room}
              >
                <i aria-hidden="true" />
                <span>{roomDoorLabel(side.room, side.label)}</span>
              </button>
            ))}
            <button
              type="button"
              className="chat-floor__portal chat-floor__portal--club mono"
              data-destination="club-terrace"
              data-menu-slot="9"
              disabled={!me || roomIsFull('club-terrace')}
              onClick={() => changeRoom('club-terrace')}
              aria-label={roomIsFull('club-terrace')
                ? 'The Higher Ground Experience Terrace is full'
                : 'Enter the Higher Ground Experience Terrace'}
            >
              <i aria-hidden="true" />
              <span>{roomDoorLabel('club-terrace', 'CLUB')}</span>
            </button>
            <button
              type="button"
              className="chat-floor__portal chat-floor__portal--club mono"
              data-destination="club-sundeck"
              data-menu-slot="10"
              disabled={!me || roomIsFull('club-sundeck')}
              onClick={() => changeRoom('club-sundeck')}
              aria-label={roomIsFull('club-sundeck')
                ? 'The HGE Sundeck Terrace is full'
                : 'Enter the HGE Sundeck Terrace'}
            >
              <i aria-hidden="true" />
              <span>{roomDoorLabel('club-sundeck', 'SUNDECK')}</span>
            </button>
          </>
        ) : inBeachClub ? (
          <>
            {beachClubLinks.map((room, index) => (
              <button
                type="button"
                className="chat-floor__portal chat-floor__portal--club mono"
                data-destination={room.room}
                data-club-slot={index + 1}
                disabled={!me || roomIsFull(room.room)}
                onClick={() => changeRoom(room.room)}
                aria-label={roomIsFull(room.room) ? `${room.name} is full` : `Enter ${room.name}`}
                key={room.room}
              >
                <i aria-hidden="true" />
                <span>{roomDoorLabel(room.room, room.label)}</span>
              </button>
            ))}
            {HOME_IS_IN_CLUB ? null : (
              <button
                type="button"
                className="chat-floor__portal chat-floor__portal--club mono"
                data-destination={HOME_ROOM}
                data-club-exit="true"
                data-club-slot={beachClubLinks.length + 1}
                disabled={!me || roomIsFull(HOME_ROOM)}
                onClick={() => changeRoom(HOME_ROOM)}
                aria-label={roomIsFull(HOME_ROOM) ? 'The main venue is full' : 'Return to the main venue'}
              >
                <i aria-hidden="true" />
                <span>{roomDoorLabel(HOME_ROOM, 'EXIT')}</span>
              </button>
            )}
          </>
        ) : (
          <button
            type="button"
            className="chat-floor__portal mono"
            data-destination={roomExit.room}
            disabled={!me || roomIsFull(roomExit.room)}
            onClick={() => changeRoom(roomExit.room)}
            aria-label={roomIsFull(roomExit.room)
              ? `${roomExitTitle} is full`
              : `Return to ${roomExitTitle}`}
          >
            <i aria-hidden="true" />
            <span>{roomDoorLabel(roomExit.room, roomExit.label)}</span>
          </button>
        )}
        {others.map((person) => (
          <button
            type="button"
            className="chat-floor__person"
            style={{ left: `${person.x}%`, top: `${person.y}%`, zIndex: Math.round(person.y) }}
            onClick={() => onProfile(person)}
            aria-label={`View ${person.name}'s profile`}
            key={person.tag}
          >
            <span className="chat-floor__walker" data-movement={person.movement}>
              {bubbles.get(person.tag) ? (
                <span className="chat-floor__bubble">{bubbleCopy(bubbles.get(person.tag) ?? '')}</span>
              ) : null}
              <PixelDancer
                avatar={person.avatar}
                skin={person.skin}
                hair={person.hair}
                dance={person.dance}
                shirt={person.shirt}
                trousers={person.trousers}
                hasWater={person.has_water}
                drink={person.drink}
                isRelaxing={person.is_relaxing}
                tattoo={person.tattoo}
                costume={person.costume}
                action={person.action}
              />
              <span className="chat-floor__name">{person.name}</span>
            </span>
          </button>
        ))}
        {me ? (
          <div
            className="chat-floor__person chat-floor__person--me"
            style={{
              left: `${mine?.x ?? 50}%`,
              top: `${mine?.y ?? 72}%`,
              zIndex: Math.round(mine?.y ?? 72) + 1,
            }}
            aria-label={`${me.display_name}, your position`}
          >
            <span className="chat-floor__walker" data-movement={me.movement}>
              {bubbles.get(me.tag) ? (
                <span className="chat-floor__bubble">{bubbleCopy(bubbles.get(me.tag) ?? '')}</span>
              ) : null}
              <PixelDancer
                avatar={me.avatar}
                skin={me.skin}
                hair={me.hair}
                dance={me.dance}
                shirt={me.shirt}
                trousers={me.trousers}
                hasWater={me.has_water}
                drink={me.drink}
                isRelaxing={me.is_relaxing}
                tattoo={me.tattoo}
                costume={me.costume}
                action={me.action}
              />
              <span className="chat-floor__name">{me.display_name} · you</span>
            </span>
          </div>
        ) : (
          <div className="chat-floor__welcome mono">Pick a name and step into the sand.</div>
        )}
        <div className="chat-floor__hint mono" aria-hidden="true">
          {me
            ? (inToilets
                ? 'TAP THE TILES TO MOVE'
                : inLounge
                  ? 'TAP THE RUG TO MOVE'
                  : inPsychedelic
                    ? 'CHANGE THE PATTERN OR MOVE AROUND'
                    : inHamMuseum
                      ? 'WATCH THE CUTTER OR MOVE AROUND'
                      : inTattooStudio
                        ? 'CHOOSE A MARK OR MOVE AROUND'
                        : inSpa
                          ? 'TOGGLE THE JETS OR MOVE BY THE POOL'
                          : inLongDrop
                            ? 'TRY THE WC DOORS OR TAP THE SAND TO MOVE'
                          : inInsideClub
                            ? 'ORDER A DRINK, SIT DOWN OR FLIP THROUGH THE CRATE'
                            : inSundeck
                              ? 'CHANGE THE DECK LIGHT, RAID THE COOLER OR WALK THE BOARDWALK'
                              : 'TAP THE SAND TO MOVE')
            : 'JOIN THE BEACH BAR TO ENTER'}
        </div>
      </div>
    </section>
  );
}

function timeLabel(value: string): string {
  const at = new Date(value);
  if (!Number.isFinite(at.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(at);
}

function fieldError(error: unknown, field: string): string | null {
  if (error instanceof ApiError) return error.fields[field] ?? error.detail;
  if (error instanceof Error) return error.message;
  return error ? 'The station did not accept that.' : null;
}

function statusLabel(phase: ChatPhase, available: boolean): string {
  switch (phase) {
    case 'idle':
    case 'loading':
      return 'Opening room';
    case 'ready':
      return 'Room open';
    case 'waiting':
      return 'Chat closed for this set';
    case 'reconnecting':
      return 'Reconnecting';
    case 'ended':
      return 'Room closed';
    case 'off_air':
      return available ? 'Chat closed for this set' : 'Off air';
    case 'failed':
      return 'Room unavailable';
  }
}

interface MessageLogProps {
  messages: readonly ChatMessage[];
  phase: ChatPhase;
  roomKey: string;
  available: boolean;
  staff: boolean;
  hidingMessageIds: ReadonlySet<number>;
  unseen: number;
  onUnseenChange: (count: number) => void;
  onRead: () => void;
  onHide: (id: number) => Promise<boolean>;
  onProfile: (sender: ChatSender) => void;
}

function MessageLog({
  messages,
  phase,
  roomKey,
  available,
  staff,
  hidingMessageIds,
  unseen,
  onUnseenChange,
  onRead,
  onHide,
  onProfile,
}: MessageLogProps) {
  const logRef = useRef<HTMLDivElement | null>(null);
  const atBottomRef = useRef(true);
  const previousIdsRef = useRef<Set<number>>(new Set());
  const [announce, setAnnounce] = useState(false);
  const [confirmHideId, setConfirmHideId] = useState<number | null>(null);

  /* History lands with announcements off. Turning the live region on one frame
     later means a screen reader hears future arrivals, not 100 old messages. */
  useEffect(() => {
    setAnnounce(false);
    const frame = window.requestAnimationFrame(() => setAnnounce(true));
    return () => window.cancelAnimationFrame(frame);
  }, [roomKey]);

  useEffect(() => {
    const node = logRef.current;
    const previous = previousIdsRef.current;
    const next = new Set(messages.map((message) => message.id));
    const added = messages.reduce(
      (count, message) => count + (previous.has(message.id) ? 0 : 1),
      0,
    );
    previousIdsRef.current = next;

    if (!node || messages.length === 0) return;
    if (previous.size === 0 || atBottomRef.current) {
      const frame = window.requestAnimationFrame(() => {
        node.scrollTop = node.scrollHeight;
        atBottomRef.current = true;
        onUnseenChange(0);
        onRead();
      });
      return () => window.cancelAnimationFrame(frame);
    }
    if (added > 0) onUnseenChange(unseen + added);
  }, [messages, onRead, onUnseenChange, unseen]);

  const onScroll = (): void => {
    const node = logRef.current;
    if (!node) return;
    const atBottom = node.scrollHeight - node.scrollTop - node.clientHeight <= 32;
    atBottomRef.current = atBottom;
    if (atBottom && unseen > 0) {
      onUnseenChange(0);
      onRead();
    }
  };

  const jumpToLatest = (): void => {
    const node = logRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
    atBottomRef.current = true;
    onUnseenChange(0);
    onRead();
    node.focus();
  };

  const emptyCopy = (() => {
    if (phase === 'waiting') return ['CHAT CLOSED FOR THIS SET', 'This room is not taking messages.'];
    if (phase === 'ended') return ['ROOM CLOSED', 'The set has finished.'];
    if (phase === 'off_air') {
      return available
        ? ['CHAT CLOSED FOR THIS SET', 'This live set does not have an open room.']
        : ['BETWEEN LIVE SETS', 'This is where chat opens when the next live set begins.'];
    }
    if (phase === 'failed') return ['ROOM UNAVAILABLE', 'We could not reach the chat service.'];
    if (phase === 'reconnecting') return ['RECONNECTING', 'Keeping the last room while we try again.'];
    return ['ROOM OPEN', 'Be the first to say hello.'];
  })();

  return (
    <div className="chat__logwrap">
      <div
        className="chat__log"
        ref={logRef}
        role="log"
        aria-label="Station chat messages"
        aria-live={announce ? 'polite' : 'off'}
        aria-relevant="additions text"
        aria-busy={phase === 'loading' ? 'true' : undefined}
        tabIndex={0}
        onScroll={onScroll}
      >
        {phase === 'loading' && messages.length === 0 ? (
          <div className="chat__empty">
            <Spinner label="Opening the station room" />
          </div>
        ) : messages.length === 0 ? (
          <div className="chat__empty">
            <div className="mono dim">{emptyCopy[0]}</div>
            <p className="t-body">{emptyCopy[1]}</p>
          </div>
        ) : (
          messages.map((message) => {
            const confirming = confirmHideId === message.id;
            const hiding = hidingMessageIds.has(message.id);
            return (
              <article className="chat-msg" key={message.id}>
                <div className="chat-msg__meta">
                  <button
                    type="button"
                    className="chat-avatar chat-avatar--message"
                    onClick={() => onProfile(message.sender)}
                    aria-label={`View ${message.sender.name}'s profile`}
                  >
                    {avatarGlyph(message.sender.avatar)}
                  </button>
                  <span className="chat-msg__sender">{message.sender.name}</span>
                  <span className="mono dim chat-msg__tag">#{message.sender.tag}</span>
                  {message.sender.rank !== 'Listener' ? (
                    <span className="mono chat-msg__rank">{message.sender.rank}</span>
                  ) : null}
                  <time className="mono dim chat-msg__time" dateTime={message.created_at}>
                    {timeLabel(message.created_at)}
                  </time>
                </div>
                <p className="chat-msg__body">{message.body}</p>
                {staff ? (
                  <div className="chat-msg__moderation">
                    {confirming ? (
                      <>
                        <button
                          type="button"
                          className="chat__textbtn mono"
                          data-chat-confirm-hide={message.id}
                          disabled={hiding}
                          onClick={() => {
                            void onHide(message.id).then((hidden) => {
                              if (hidden) {
                                setConfirmHideId(null);
                                logRef.current?.focus();
                              }
                            });
                          }}
                        >
                          {hiding ? 'Hiding…' : 'Confirm hide'}
                        </button>
                        <button
                          type="button"
                          className="chat__textbtn mono"
                          disabled={hiding}
                          onClick={() => {
                            setConfirmHideId(null);
                            window.requestAnimationFrame(() => {
                              logRef.current
                                ?.querySelector<HTMLElement>(`[data-chat-hide="${message.id}"]`)
                                ?.focus();
                            });
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="chat__textbtn mono"
                        data-chat-hide={message.id}
                        onClick={() => {
                          setConfirmHideId(message.id);
                          window.requestAnimationFrame(() => {
                            logRef.current
                              ?.querySelector<HTMLElement>(
                                `[data-chat-confirm-hide="${message.id}"]`,
                              )
                              ?.focus();
                          });
                        }}
                        aria-label={`Hide message from ${message.sender.name}`}
                      >
                        Hide
                      </button>
                    )}
                  </div>
                ) : null}
              </article>
            );
          })
        )}
      </div>

      {unseen > 0 ? (
        <button type="button" className="btn chat__jump" onClick={jumpToLatest}>
          {unseen} new {unseen === 1 ? 'message' : 'messages'}
        </button>
      ) : null}
    </div>
  );
}

type AccountMode = 'login' | 'register' | 'inventory';

function AccountPanel({
  chat,
  mode,
  onMode,
  onClose,
}: {
  chat: ChatValue;
  mode: AccountMode;
  onMode: (mode: AccountMode) => void;
  onClose: () => void;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const error = fieldError(chat.actionError, 'username')
    ?? fieldError(chat.actionError, 'password');

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const ok = mode === 'register'
      ? await chat.registerAccount(username, password)
      : await chat.loginAccount(username, password);
    if (ok) {
      setPassword('');
      onClose();
    }
  };

  return (
    <section className="chat-accountpanel" aria-label="Listener account">
      <header>
        <div>
          <span className="mono">Listener account</span>
          <strong>{mode === 'inventory' ? `@${chat.account?.username}` : mode === 'register' ? 'Save this profile' : 'Welcome back'}</strong>
        </div>
        <button type="button" onClick={onClose} aria-label="Close account panel">×</button>
      </header>
      {mode === 'inventory' && chat.account ? (
        <div className="chat-accountpanel__body">
          <p className="mono dim">Your collected clothes stay with this account on every device.</p>
          <div className="chat-inventory">
            {chat.account.inventory.length > 0 ? chat.account.inventory.map((item) => (
              <article key={item.slug} data-token={item.token}>
                <i aria-hidden="true" />
                <div><strong>{item.name}</strong><span className="mono dim">{item.description}</span></div>
              </article>
            )) : <p className="mono dim">Your inventory is empty. Watch for a live drop.</p>}
          </div>
          <button
            type="button"
            className="chat__textbtn mono"
            disabled={chat.accountBusy}
            onClick={() => void chat.logoutAccount().then((ok) => { if (ok) onClose(); })}
          >
            {chat.accountBusy ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      ) : (
        <form className="chat-accountpanel__body" onSubmit={(event) => void submit(event)}>
          <label className="mono" htmlFor="chat-account-username">Username</label>
          <input
            id="chat-account-username"
            value={username}
            minLength={3}
            maxLength={24}
            autoComplete="username"
            aria-describedby="chat-account-requirements"
            onChange={(event) => { setUsername(event.target.value); chat.clearActionError(); }}
            required
          />
          <label className="mono" htmlFor="chat-account-password">Password</label>
          <input
            id="chat-account-password"
            type="password"
            value={password}
            minLength={8}
            maxLength={128}
            autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
            aria-describedby="chat-account-requirements"
            onChange={(event) => { setPassword(event.target.value); chat.clearActionError(); }}
            required
          />
          <p className="mono dim chat-accountpanel__requirements" id="chat-account-requirements">
            Username: 3–24 characters · Password: at least 8 characters
          </p>
          <button type="submit" className="btn" disabled={chat.accountBusy}>
            {chat.accountBusy ? 'One sec…' : mode === 'register' ? 'Create account' : 'Sign in'}
          </button>
          {error ? <p className="chat__fielderror" role="alert">{error}</p> : null}
          <button
            type="button"
            className="chat__textbtn chat-accountpanel__switch mono"
            onClick={() => { chat.clearActionError(); onMode(mode === 'register' ? 'login' : 'register'); }}
          >
            {mode === 'register' ? 'I already have an account' : chat.me ? 'Create an account instead' : 'Join first to create an account'}
          </button>
        </form>
      )}
    </section>
  );
}

function LiveDropBanner({
  chat,
  onNeedAccount,
}: {
  chat: ChatValue;
  onNeedAccount: () => void;
}) {
  const drop = chat.drops[0];
  if (!drop) return null;
  const soldOut = drop.remaining <= 0;
  return (
    <section className="chat-drop" aria-label="Live clothing drop">
      <div className="chat-drop__item" data-token={drop.item.token}><i aria-hidden="true" /></div>
      <div>
        <span className="mono">Live drop · {drop.remaining}/{drop.quantity} left</span>
        <strong>{drop.item.name}</strong>
      </div>
      <button
        type="button"
        className="btn"
        disabled={soldOut || drop.claimed_by_me || chat.dropBusy}
        onClick={() => {
          if (!chat.account) onNeedAccount();
          else void chat.claimDrop(drop.id);
        }}
      >
        {drop.claimed_by_me ? 'Collected' : soldOut ? 'Sold out' : chat.dropBusy ? 'Claiming…' : chat.account ? 'Claim tee' : 'Sign in to claim'}
      </button>
    </section>
  );
}

function OpenLiveChatPanel({ chat }: { chat: ChatValue }) {
  const narrow = useNarrow();
  const panelRef = useRef<HTMLElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(
    document.activeElement instanceof HTMLElement ? document.activeElement : null,
  );
  const [displayName, setDisplayName] = useState('');
  const [avatar, setAvatar] = useState<ChatAvatar>('signal');
  const [skin, setSkin] = useState<ChatSkin>('tone-6');
  const [hair, setHair] = useState<ChatHair>('dark-brown');
  const [dance, setDance] = useState<ChatDance>('sway');
  const [movement, setMovement] = useState<ChatMovement>('rooted');
  const [shirt, setShirt] = useState<ChatShirt>('hgr-tee');
  const [trousers, setTrousers] = useState<ChatTrousers>('black-jeans');
  const [costume, setCostume] = useState<ChatCostume>('none');
  const [editingName, setEditingName] = useState(false);
  const [unseen, setUnseen] = useState(0);
  const [confirmClose, setConfirmClose] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<ChatSender | null>(null);
  const [accountMode, setAccountMode] = useState<AccountMode | null>(null);
  const openAccount = (): void => {
    setAccountMode(chat.account ? 'inventory' : chat.me ? 'register' : 'login');
  };
  const availableShirts = useMemo(() => {
    const tokens = new Set<ChatShirt>([
      'hgr-tee', 'mesh', 'track', 'sunset', 'workshirt', 'no-shirt',
      // Ordinary stock. 'hgr-limited' stays out of this list on purpose: it is
      // the claimable drop and arrives through the inventory below.
      'rr-tee', 'hgr-radio', 'flytree',
    ]);
    for (const item of chat.account?.inventory ?? []) {
      if (item.kind === 'shirt') tokens.add(item.token);
    }
    if (chat.me?.shirt) tokens.add(chat.me.shirt);
    return tokens;
  }, [chat.account, chat.me?.shirt]);

  const focusAfterRender = (selector: string): void => {
    window.requestAnimationFrame(() => {
      panelRef.current?.querySelector<HTMLElement>(selector)?.focus();
    });
  };

  const closePanel = useCallback(() => {
    chat.preserveUnread(unseen);
    chat.closeChat();
  }, [chat.closeChat, chat.preserveUnread, unseen]);

  const updateUnseen = useCallback((count: number) => {
    setUnseen(count);
    if (count > 0) chat.preserveUnread(count);
  }, [chat.preserveUnread]);

  const beginProfileEdit = (): void => {
    chat.clearActionError();
    setDisplayName(chat.me?.display_name ?? '');
    setAvatar(chat.me?.avatar ?? 'signal');
    setSkin(chat.me?.skin ?? 'tone-6');
    setHair(chat.me?.hair ?? 'dark-brown');
    setDance(chat.me?.dance ?? 'sway');
    setMovement(chat.me?.movement ?? 'rooted');
    setShirt(chat.me?.shirt ?? 'hgr-tee');
    setTrousers(chat.me?.trousers ?? 'black-jeans');
    setCostume(chat.me?.costume ?? 'none');
    setEditingName(true);
    focusAfterRender('#chat-display-name');
  };

  /* Focus enters deliberately and returns to whichever launcher opened us. */
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const target = panelRef.current?.querySelector<HTMLElement>('[data-chat-autofocus]');
      (target ?? panelRef.current)?.focus();
    });
    return () => {
      window.cancelAnimationFrame(frame);
      if (restoreFocusRef.current?.isConnected) restoreFocusRef.current.focus();
    };
  }, []);

  /* Escape closes everywhere. Only the phone traps Tab; desktop deliberately
     leaves the persistent player and the rest of the page reachable. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePanel();
        return;
      }
      if (!narrow || event.key !== 'Tab') return;
      const node = panelRef.current;
      if (!node) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!active || !node.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [closePanel, narrow]);

  useEffect(() => {
    if (!narrow) return;
    const restore = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = restore;
    };
  }, [narrow]);

  const currentFloorRoom = chat.me?.floor_room ?? HOME_ROOM;
  const roomKey = `${chat.room?.id ?? 'no-room'}:${currentFloorRoom}`;
  const writable = Boolean(chat.active && chat.room?.writable);
  const choosingName = !chat.me || editingName;
  const nameError = choosingName ? fieldError(chat.actionError, 'display_name') : null;
  const messageError = chat.me && !editingName ? fieldError(chat.actionError, 'body') : null;
  const status = statusLabel(chat.phase, chat.available);

  const submitName = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (displayName.trim().length < 2) return;
    const joined = await chat.join({
      display_name: displayName,
      avatar,
      skin,
      hair,
      dance,
      movement,
      shirt,
      trousers,
      costume,
    });
    if (joined) {
      setDisplayName('');
      setEditingName(false);
      focusAfterRender('#chat-message');
    }
  };

  const submitMessage = async (): Promise<void> => {
    if (!chat.draft.trim() || chat.draft.length > 280 || chat.sending) return;
    await chat.send(chat.draft);
  };

  const onComposerKey = (event: ReactKeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    void submitMessage();
  };

  return (
    <aside
      className="chat-panel"
      id={chat.panelId}
      ref={panelRef}
      role={narrow ? 'dialog' : 'region'}
      aria-modal={narrow ? 'true' : undefined}
      aria-labelledby="live-chat-title"
      tabIndex={-1}
    >
      <VenueLightFilters />
      <header className="chat__head">
        <div className="chat__heading">
          <h2 className="chat__title" id="live-chat-title">Station chat</h2>
          <div className="mono chat__status" role="status">
            {status}{chat.room ? ` · ${chat.room.id}` : ''}
          </div>
        </div>
        <div className="chat__headactions">
          <button
            type="button"
            className="chat__accounttop mono"
            onClick={openAccount}
          >
            {chat.account ? `@${chat.account.username}` : chat.me ? 'Create account' : 'Sign in'}
          </button>
          <button
            type="button"
            className="chat__close"
            onClick={closePanel}
            aria-label="Close live chat"
            data-chat-autofocus
          >
            ×
          </button>
        </div>
      </header>

      {chat.room ? <div className="chat__roomtitle">{chat.room.title}</div> : null}

      {chat.pollError ? (
        <ErrorNote error={chat.pollError} what="the live room" onRetry={chat.retry} retryLabel="Reconnect" />
      ) : null}

      {chat.staff ? (
        <div className="chat__staff">
          <span className="mono">Staff controls</span>
          <button
            type="button"
            className="btn-ghost"
            disabled={chat.dropBusy}
            onClick={() => {
              const activeDrop = chat.drops[0];
              if (activeDrop) void chat.endDrop(activeDrop.id);
              else void chat.startDrop();
            }}
          >
            {chat.dropBusy ? 'Updating drop…' : chat.drops[0] ? 'End HGR tee drop' : 'Drop 20 HGR tees'}
          </button>
          {chat.room?.closable && writable ? (
            confirmClose ? (
              <div className="chat__staffactions">
                <button
                  type="button"
                  className="btn"
                  data-chat-confirm-close
                  disabled={chat.closingRoom}
                  onClick={() => {
                    void chat.closeRoom().then((closed) => {
                      if (closed) {
                        setConfirmClose(false);
                        focusAfterRender('[data-chat-autofocus]');
                      }
                    });
                  }}
                >
                  {chat.closingRoom ? 'Closing…' : 'Confirm close'}
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={chat.closingRoom}
                  onClick={() => {
                    setConfirmClose(false);
                    focusAfterRender('[data-chat-close-room]');
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="btn-ghost"
                data-chat-close-room
                onClick={() => {
                  setConfirmClose(true);
                  focusAfterRender('[data-chat-confirm-close]');
                }}
              >
                Close chat
              </button>
            )
          ) : null}
        </div>
      ) : null}

      {chat.moderationError ? (
        <ErrorNote error={chat.moderationError} what="that staff action" />
      ) : null}

      {selectedProfile ? (
        <ProfileCard
          name={selectedProfile.name}
          tag={selectedProfile.tag}
          profile={selectedProfile}
          onClose={() => setSelectedProfile(null)}
        />
      ) : null}

      {accountMode ? (
        <AccountPanel
          chat={chat}
          mode={accountMode}
          onMode={setAccountMode}
          onClose={() => { chat.clearActionError(); setAccountMode(null); }}
        />
      ) : null}

      <LiveDropBanner
        chat={chat}
        onNeedAccount={() => setAccountMode(chat.me ? 'register' : 'login')}
      />

      <div className="chat-social">
        <DanceFloor
          me={chat.me}
          messages={chat.messages}
          people={chat.people}
          rooms={chat.venueRooms}
          onMove={chat.move}
          onWater={chat.water}
          onDrink={chat.drink}
          waterBusy={chat.waterBusy}
          onRelax={chat.relax}
          relaxBusy={chat.relaxBusy}
          onTattoo={chat.tattoo}
          tattooBusy={chat.tattooBusy}
          onAction={chat.act}
          actionBusy={chat.actionBusy}
          onTrackId={() => chat.send('TRACK ID? WHAT IS PLAYING?')}
          spaDoor={chat.spaDoor}
          spaDoorBusy={chat.spaDoorBusy}
          onSpaBouncer={chat.setSpaBouncer}
          onSpaRequest={chat.requestSpaEntry}
          onSpaDecision={chat.decideSpaEntry}
          onProfile={setSelectedProfile}
          onEditLook={beginProfileEdit}
          isAdmin={Boolean(chat.account?.is_admin)}
        />
        <section className="chat-social__rail" aria-labelledby="chat-rail-title">
          <div className="chat-social__railhead">
            <strong id="chat-rail-title">
              {currentFloorRoom === 'club-sundeck' ? 'Main room chat' : 'Room chat'}
            </strong>
            <div className="chat-social__railactions">
              <span className="mono dim">
                {chat.messages.length} {currentFloorRoom === 'club-sundeck' ? 'saved' : 'recent'}
              </span>
              <button
                type="button"
                className="chat__textbtn mono"
                onClick={openAccount}
              >
                {chat.account ? `Inventory ${chat.account.inventory.length}` : chat.me ? 'Create account' : 'Sign in'}
              </button>
            </div>
          </div>
          <MessageLog
            key={roomKey}
            messages={chat.messages}
            phase={chat.phase}
            roomKey={roomKey}
            available={chat.available}
            staff={Boolean(chat.staff)}
            hidingMessageIds={chat.hidingMessageIds}
            unseen={unseen}
            onUnseenChange={updateUnseen}
            onRead={chat.markRead}
            onHide={chat.hideMessage}
            onProfile={setSelectedProfile}
          />
        </section>
      </div>

      {writable ? (
        <div className="chat__compose">
          <div className="mono dim chat__delay">
            During live sets, the stream runs about 25 seconds behind this room.
          </div>

          {choosingName ? (
            <form className="chat__nameform" onSubmit={(event) => void submitName(event)}>
              <label className="mono" htmlFor="chat-display-name">
                {chat.me ? 'Change your display name' : 'Choose a display name'}
              </label>
              <div className="chat__sendrow">
                <input
                  id="chat-display-name"
                  value={displayName}
                  onChange={(event) => {
                    setDisplayName(event.target.value);
                    chat.clearActionError();
                  }}
                  minLength={2}
                  maxLength={24}
                  autoComplete="nickname"
                  aria-invalid={nameError ? 'true' : undefined}
                  aria-describedby={nameError ? 'chat-name-error' : 'chat-name-hint'}
                  required
                />
                <button type="submit" className="btn" disabled={chat.joining || displayName.trim().length < 2}>
                  {chat.joining ? 'Saving…' : chat.me ? 'Save' : 'Join'}
                </button>
              </div>
              <AvatarPicker
                value={avatar}
                shirt={shirt}
                trousers={trousers}
                isAdmin={Boolean(chat.account?.is_admin)}
                onChange={setAvatar}
              />
              {isAdminAvatar(avatar) ? (
                <p className="mono dim chat-character-lock">
                  Complete admin character · outfit included
                </p>
              ) : (
                <>
                  {/* Hidden for the two PixelLab collectibles above: they are
                      bitmap spritesheets, so no token can retint their skin. */}
                  <ComplexionPicker
                    legend="Choose your skin tone"
                    options={SKINS}
                    value={skin}
                    onChange={setSkin}
                  />
                  <ComplexionPicker
                    legend="Choose your hair colour"
                    options={HAIRS}
                    value={hair}
                    onChange={setHair}
                  />
                </>
              )}
              {isAdminAvatar(avatar) ? null : (
                <WardrobePicker
                  avatar={avatar}
                  skin={skin}
                  hair={hair}
                  dance={dance}
                  shirt={shirt}
                  trousers={trousers}
                  costume={costume}
                  onShirtChange={setShirt}
                  onTrousersChange={setTrousers}
                  onCostumeChange={setCostume}
                  availableShirts={availableShirts}
                />
              )}
              <DancePicker
                avatar={avatar}
                shirt={shirt}
                trousers={trousers}
                value={dance}
                onChange={setDance}
              />
              <MovementPicker
                avatar={avatar}
                dance={dance}
                shirt={shirt}
                trousers={trousers}
                value={movement}
                onChange={setMovement}
              />
              <p className="mono dim chat__hint" id="chat-name-hint">
                Continue as a guest, or save this profile with an account afterwards.
              </p>
              {!chat.me ? (
                <button
                  type="button"
                  className="chat__textbtn mono"
                  onClick={() => { chat.clearActionError(); setAccountMode('login'); }}
                >
                  Sign in to a saved account
                </button>
              ) : null}
              {nameError ? <p className="chat__fielderror" id="chat-name-error" role="alert">{nameError}</p> : null}
              {chat.me ? (
                <button
                  type="button"
                  className="chat__textbtn mono"
                  disabled={chat.joining}
                  onClick={() => {
                    chat.clearActionError();
                    setDisplayName('');
                    setAvatar(chat.me?.avatar ?? 'signal');
                    setSkin(chat.me?.skin ?? 'tone-6');
                    setHair(chat.me?.hair ?? 'dark-brown');
                    setDance(chat.me?.dance ?? 'sway');
                    setMovement(chat.me?.movement ?? 'rooted');
                    setShirt(chat.me?.shirt ?? 'hgr-tee');
                    setTrousers(chat.me?.trousers ?? 'black-jeans');
                    setCostume(chat.me?.costume ?? 'none');
                    setEditingName(false);
                    focusAfterRender('#chat-message');
                  }}
                >
                  Cancel
                </button>
              ) : null}
            </form>
          ) : (
            <form
              className="chat__messageform"
              onSubmit={(event) => {
                event.preventDefault();
                void submitMessage();
              }}
            >
              <label className="vh" htmlFor="chat-message">
                {currentFloorRoom === 'club-sundeck' ? 'Message the main room' : 'Message this room'}
              </label>
              <textarea
                id="chat-message"
                rows={2}
                maxLength={280}
                value={chat.draft}
                onChange={(event) => {
                  chat.setDraft(event.target.value);
                  chat.clearActionError();
                }}
                onKeyDown={onComposerKey}
                aria-describedby="chat-message-count chat-message-hint"
                aria-invalid={messageError ? 'true' : undefined}
                placeholder={`Message as ${chat.me?.display_name ?? 'listener'}`}
                disabled={chat.sending}
              />
              <div className="chat__sendmeta">
                <span className="mono dim" id="chat-message-hint">Enter sends · Shift+Enter adds a line</span>
                <span className="mono dim" id="chat-message-count">{280 - chat.draft.length}</span>
              </div>
              {messageError ? <p className="chat__fielderror" role="alert">{messageError}</p> : null}
              <button
                type="submit"
                className="btn chat__send"
                disabled={chat.sending || !chat.draft.trim()}
              >
                {chat.sending ? 'Sending…' : 'Send message'}
              </button>
            </form>
          )}
        </div>
      ) : (
        <div className="chat__closed mono" role="status">
          Chat is temporarily read-only.
        </div>
      )}
    </aside>
  );
}

/** Keep the focus-management component genuinely unmounted while chat is shut. */
export function LiveChatPanel() {
  const chat = useChat();
  return chat.open ? <OpenLiveChatPanel chat={chat} /> : null;
}
